import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, catchError, combineLatest, defer, expand, filter, forkJoin, ignoreElements, map, merge, of, reduce, shareReplay, switchMap, take, tap, throwError } from 'rxjs';
import { FormFields } from './dynamic-forms.interface2';
import { Bundle, BundleEntry, FhirResource, Observation, Resource, CodeableConcept, Dosage, ChargeItemDefinition, Quantity, Schedule, Practitioner, Medication } from 'fhir/r4';
import {
  backendEndPointToken,
  patientBackendEndPointToken,
  fhirDefaultCountToken,
  fhirMaxAllAtOnceCountToken,
  serviceRequestCategoriesToken,
  substanceCodeCategoriesToken,
  terminologyCanonicalUrlBaseToken
} from '../app.config';
import { Coding } from 'fhir/r4';
import { GroupField, IndividualField } from '../shared/dynamic-forms.interface2';
import { Identifier, Reference } from 'fhir/r4';
import { ActivityDefinition } from 'fhir/r4b';
import { StateService } from './state.service';
import { DisableCache, DisableCountParam, DisableSortParam, LoadingUIEnabled } from '../loading.interceptor';
import { AuthService } from './auth/auth.service';
import { Organization } from 'fhir/r4';

export type FhirSearchOrValue = { or: Array<string | number | boolean> };
export type FhirSearchParamValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean>
  | FhirSearchOrValue
  | null
  | undefined;

export interface ThreeTierFhirQuery {
  resourceType: string;
  /**
   * Tier-1 URL query params (server search). Use this to build the URL.
   * Example: { patient: '123', status: 'final', category: 'laboratory' }
   */
  urlSearchObject?: Record<string, FhirSearchParamValue>;
  /** Back-compat alias for urlSearchObject. */
  queryParams?: Record<string, FhirSearchParamValue>;

  /**
   * URL keys whose array values should be encoded as OR via comma-joining.
   * Example: { status: ['final','registered'] } with urlOrKeys:['status'] => `status=final,registered`.
   * NOTE: This does not affect `FhirSearchOrValue` (e.g. {status:{or:[...]}}) which always uses comma-joining.
   */
  urlOrKeys?: string[];

  /**
   * Local re-filtering (do NOT trust server filtering). Supports deep paths with dots.
   * Example: { key: 'basedOn.reference', value: 'ServiceRequest/123' }
   */
  querySearchObject?: { key: string; value: any } | Array<{ key: string; value: any }>;
  /** Back-compat aliases for local filtering. */
  filterKey?: string;
  filterValue?: any;

  patientId?: string | null;
  useCurrentPatientId?: boolean;
  /**
   * When scope resolves to patient, tier-3 should include a patient reference filter.
   * Most resources use `subject=Patient/{id}` (Observation/ServiceRequest/DiagnosticReport),
   * while others use `patient={id}` (Encounter, etc). Default: `subject`.
   */
  patientScopeSearchParam?: 'subject' | 'patient' | string;
  scope?: 'auto' | 'patient' | 'orgWide';
  count?: number;
  format?: 'json';

  /**
   * Tier-1 can optionally follow `Bundle.link[relation=next]` to page through server results.
   * Use this when the server enforces a small page size (e.g. 50) and a single successful
   * search response may still miss the record you're looking for.
   *
   * Notes:
   * - In streaming mode, paging primarily helps by progressively upserting into StateService,
   *   so `stateFiltered$` re-emits as more pages arrive.
   * - In one-shot mode, paging aggregates pages before returning.
   */
  tier1FollowNextLinks?: boolean;
  /** Safety cap for tier-1 pagination when `tier1FollowNextLinks` is enabled. Default: 10 pages. */
  tier1MaxPages?: number;

  /**
    * Tier-3 can optionally follow `Bundle.link[relation=next]` to page through server results.
   * Useful when server enforces `_count` limits and a single page may miss the record you're looking for.
   */
  tier3FollowNextLinks?: boolean;
  /** Safety cap for tier-3 pagination when `tier3FollowNextLinks` is enabled. Default: 10 pages. */
  tier3MaxPages?: number;

  /**
   * If true: when tier-1 fails (e.g. server rejects an unsupported search parameter), tier-3 will run
   * when the locally-filtered state has fewer than `expectedMinLength` results (instead of only when empty).
   *
   * Default: false (backwards compatible).
   */
  tier3TriggerWhenBelowExpectedMinOnTier1Failure?: boolean;

  /**
   * Optional: if provided, pagination (tier-1 and/or tier-3 when enabled) will continue until
   * locally-filtered results reach this minimum.
   *
   * If not provided and local filters exist, defaults to 1 (keep paging until a match is found).
   */
  expectedMinLength?: number;

  /** Optional HttpContext to control loader/caching behavior (e.g., silent validations). */
  context?: HttpContext;
}

export interface ServiceRequestCodeOption {
  code: string;
  display: string;
  system: string;
}

export interface ServiceRequestCodeOptionWithStatus extends ServiceRequestCodeOption {
  category?: string;
  active?: boolean;
  hasActivityDefinition?: boolean;
}

export interface MedicationMatchOptions {
  matchBatchId?: boolean;      // If true, forces it to only return medications with the exact same lotNumber
  excludeExpired?: boolean;    // If true, filters out batches where expirationDate is in the past
  excludeOutOfStock?: boolean; // If true, filters out batches where totalRemaining <= 0
}

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  private readonly stateService = inject(StateService);
  private readonly auth = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
  private readonly fhirDefaultCount = inject(fhirDefaultCountToken, { optional: true }) ?? 200;
  private readonly fhirMaxAllAtOnceCount = inject(fhirMaxAllAtOnceCountToken, { optional: true }) ?? 10;
  private readonly serviceRequestCodesCache = new Map<string, Observable<ServiceRequestCodeOption[]>>();
  private readonly substanceCodeValueSetCache = new Map<string, Observable<Coding[]>>();
  private allServiceRequestCodesActive$?: Observable<ServiceRequestCodeOption[]>;
  private serviceRequestCodeToCategory$?: Observable<Map<string, string>>;
  private allServiceRequestCodesWithStatus$?: Observable<ServiceRequestCodeOptionWithStatus[]>;
  private lastSequenceTimestamp = 0;
  private sequenceSubCounter = 0;


  observation_units = [
    "kg",     // kilograms
    "g",      // grams
    "mg",     // milligrams
    "ug",     // micrograms
    "L",      // liters
    "mL",     // milliliters
    "cm",     // centimeters
    "mm",     // millimeters
    "m",      // meters
    "mmHg",   // millimeters of mercury (blood pressure)
    "bpm",    // beats per minute (heart rate)
    "/min",   // per minute (respiration rate)
    "°C",     // degrees Celsius (body temperature)
    "%",      // percent (e.g., oxygen saturation)
    "mol/L",  // moles per liter (common for electrolytes)
    "mmol/L", // millimoles per liter (common for glucose, cholesterol)
    "mg/dL",  // milligrams per deciliter
    "ng/mL",  // nanograms per milliliter
    "U/L",    // units per liter (e.g., liver enzymes)
    "IU/L",   // international units per liter
    "mEq/L",  // milliequivalents per liter
    "cm[H2O]",// centimeters of water (respiratory pressure)
    "s",      // seconds
    "min",    // minutes
    "h",      // hours
    "d"       // days
  ]

  /**
   * FHIR R4 ServiceRequest intent-to-status mapping.
   * Maps each intent value to its allowed status values based on workflow semantics.
   */
  serviceRequestIntentToStatus: Record<string, string> = {
    'proposal': 'draft',
    'plan': 'draft',
    'order': 'active',
    'instance-order': 'active',
    'option': 'draft'
  };

  durationUnits: string[][] = [
    ['second', 'second', '秒'],
    ['min', 'minute', 'minute', '分钟'],
    ['h', 'hour', 'hour', '小时'],
    ['d', 'day', 'day', '天'],
    ['wk', 'week', 'week', '星期'],
    ['mo', 'month', 'month', '月'],
    ['a', 'year', 'year', '']
  ];
  private readonly dynamicFieldDelimiter = '$#$';

  constructor() { }

  ensureArray<T>(value: T | T[] | null | undefined): T[] {
    if (Array.isArray(value)) {
      return value;
    }
    return value == null ? [] : [value];
  }

  stripEmptyValues<T>(input: T): T {
    const cleaned = this.removeEmptyValues(input);
    return (cleaned as T);
  }

  codingFromDelimited(value: string | Coding | null | undefined): Coding | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      const triplet = this.splitDelimitedTriplet(value);
      if (!triplet) {
        return null;
      }
      const [code, display, system] = triplet;
      const result: Coding = {};
      if (code) {
        result.code = code;
      }
      if (display) {
        result.display = display;
      }
      if (system) {
        result.system = system;
      }
      return Object.keys(result).length ? result : null;
    }
    return value;
  }

  codeableConceptFromDelimited(
    value: string | CodeableConcept | Coding | null | undefined
  ): CodeableConcept | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      const coding = this.codingFromDelimited(value);
      if (!coding) {
        return null;
      }
      return {
        coding: [coding],
        text: coding.display || coding.code
      };
    }
    if (Array.isArray((value as CodeableConcept)?.coding) || (value as CodeableConcept)?.text) {
      return value as CodeableConcept;
    }
    const coding = this.codingFromDelimited(value as Coding);
    return coding ? { coding: [coding], text: coding.display || coding.code } : null;
  }

  delimitedFromCoding(coding: Coding | null | undefined): string | null {
    if (!coding) {
      return null;
    }
    const code = coding.code ?? '';
    const display = coding.display ?? '';
    const system = coding.system ?? '';
    return [code, display, system].join(this.dynamicFieldDelimiter);
  }

  delimitedFromCodeableConcept(concept: CodeableConcept | null | undefined): string | null {
    if (!concept) {
      return null;
    }
    const coding = this.ensureArray(concept.coding)[0];
    if (!coding) {
      return concept.text ?? null;
    }
    return this.delimitedFromCoding(coding);
  }

  referenceFromDelimited(value: string | Reference | null | undefined): Reference | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      const parts = this.splitDelimitedTriplet(value);
      if (!parts) {
        return null;
      }
      const [reference, display, type] = parts;
      const ref: Reference = {};
      if (reference) {
        ref.reference = reference;
      }
      if (display) {
        ref.display = display;
      }
      if (type) {
        ref.type = type;
      }
      return Object.keys(ref).length ? ref : null;
    }
    return value;
  }

  delimitedFromReference(value: Reference | string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      return value;
    }
    const reference = value.reference ?? '';
    const display = value.display ?? '';
    const type = value.type ?? '';
    return [reference, display, type].join(this.dynamicFieldDelimiter);
  }

  private removeEmptyValues(value: any): any {
    if (Array.isArray(value)) {
      const next = value
        .map(item => this.removeEmptyValues(item))
        .filter(item => !this.isValueEmpty(item));
      return next.length ? next : undefined;
    }
    if (value && typeof value === 'object') {
      const result: any = {};
      Object.keys(value).forEach(key => {
        const cleaned = this.removeEmptyValues(value[key]);
        if (!this.isValueEmpty(cleaned)) {
          result[key] = cleaned;
        }
      });
      return Object.keys(result).length ? result : undefined;
    }
    return this.isValueEmpty(value) ? undefined : value;
  }

  private isValueEmpty(value: any): boolean {
    if (value === undefined || value === null) {
      return true;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return true;
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (value && typeof value === 'object') {
      return Object.keys(value).length === 0;
    }
    return false;
  }

  private splitDelimitedTriplet(raw: string | null | undefined): [string, string, string] | null {
    if (!raw) {
      return null;
    }
    const parts = raw.split(this.dynamicFieldDelimiter);
    const [first = '', second = '', third = ''] = parts.map(p => p?.trim?.() ?? '');
    if (!first && !second && !third) {
      return null;
    }
    return [first, second, third];
  }
  /**
 * Returns a FHIR Dosage.timing object from a value object containing dosageTiming.
 *
 * Example input:
 * {
 *   dosageTiming: {
 *     duration: 7,
 *     durationUnit: 'day',
 *     timingCode: { code: 'BID', display: 'Two times a day', system: 'http://...' }
 *   }
 * }
 *
 * Output:
 * {
 *   timing: {
 *     duration: 7,
 *     durationUnit: 'day',
 *     code: {
 *       coding: [{ code: 'BID', display: 'Two times a day', system: 'http://...' }],
 *       text: { code: 'BID', display: 'Two times a day', system: 'http://...' }
 *     }
 *   }
 * }
 */
  commonFormFields: Record<string, FormFields | FormFields[]> = {
    'address': <GroupField>{
      generalProperties: {
        fieldApiName: 'address',
        fieldName: 'Address',
        fieldLabel: 'Address',
        fieldType: 'IndividualField',
        auth: { read: 'all', write: 'doctor, nurse, admin' },
        isArray: false,
        isGroup: true
      },
      keys: ['line', 'city', 'state', 'country', 'postalCode'],
      groupFields: {
        line: <IndividualField>{
          generalProperties: {
            fieldApiName: 'line',
            fieldName: 'Address Line',
            fieldLabel: 'Address Line',
            fieldType: 'IndividualField',
            inputType: 'text',
            isArray: true,
            isGroup: false
          },
          data: ''
        },
        city: <IndividualField>{
          generalProperties: {
            fieldApiName: 'city',
            fieldName: 'City',
            fieldLabel: 'City',
            fieldType: 'IndividualField',
            inputType: 'text',
            isArray: false,
            isGroup: false
          },
          data: ''
        },
        state: <IndividualField>{
          generalProperties: {
            fieldApiName: 'state',
            fieldName: 'State/Province',
            fieldLabel: 'State/Province',
            fieldType: 'IndividualField',
            inputType: 'text',
            isArray: false,
            isGroup: false
          },
          data: ''
        },
        country: <IndividualField>{
          generalProperties: {
            fieldApiName: 'country',
            fieldName: 'Country',
            fieldLabel: 'Country',
            fieldType: 'IndividualField',
            inputType: 'text',
            isArray: false,
            isGroup: false
          },
          data: ''
        },
        postalCode: <IndividualField>{
          generalProperties: {
            fieldApiName: 'postalCode',
            fieldName: 'Postal Code',
            fieldLabel: 'Postal Code',
            fieldType: 'IndividualField',
            inputType: 'text',
            isArray: false,
            isGroup: false
          },
          data: ''
        }
      }
    },
    'dosage': [
      <GroupField>{
        generalProperties: {
          fieldApiName: 'dosageTiming',
          fieldName: 'Dosage Timing',
          fieldLabel: 'When to take the medication',
          groupFieldsHint: 'When to take the medication e.g., "Once daily in the morning for 7 days". Use Duration + Duration Unit together to describe how long the patient should take it.',
          isGroup: true,
          isArray: false,
          fieldType: 'IndividualField'
        },
        keys: ['timingCode', 'duration', 'durationUnit'],
        groupFields: {
          timingCode: <IndividualField>{
            generalProperties: {
              fieldApiName: 'timingCode',
              fieldName: 'Timing Code',
              fieldLabel: 'Timing Code',
              fieldType: 'CodeableConceptField',
              inputType: 'text',
              fieldPlaceholder: 'e.g., morning, evening, before meal',
              isArray: false,
              isGroup: false,
              allowedOthers: true
            },
            data: [
              { code: 'BID', display: 'Two times a day', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'TID', display: 'Three times a day', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'QID', display: 'Four times a day', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'AM', display: 'Every morning', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'PM', display: 'Every afternoon', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'QD', display: 'Every day', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'QOD', display: 'Every other day', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'Q1H', display: 'Every hour', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'Q2H', display: 'Every 2 hours', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'Q3H', display: 'Every 3 hours', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'Q4H', display: 'Every 4 hours', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'Q6H', display: 'Every 6 hours', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'Q8H', display: 'Every 8 hours', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'BED', display: 'At bedtime', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'WK', display: 'Weekly', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' },
              { code: 'MO', display: 'Monthly', system: 'http://terminology.hl7.org/CodeSystem/v3-GTSAbbreviation' }
            ]
          },
          duration: <IndividualField>{
            generalProperties: {
              fieldApiName: 'duration',
              fieldName: 'Duration',
              fieldLabel: 'Duration',
              fieldType: 'IndividualField',
              inputType: 'number',
              fieldPlaceholder: 'e.g., 1, 2, 3',
              fieldHint: 'Enter the numeric course length and pair it with Duration Unit',
              isArray: false,
              isGroup: false
            },
            data: ''
          },
          durationUnit: <IndividualField>{
            generalProperties: {
              fieldApiName: 'durationUnit',
              fieldName: 'Duration Unit',
              fieldLabel: 'Duration Unit',
              fieldType: 'CodeField',
              inputType: 'text',
              fieldPlaceholder: 'e.g., days, weeks',
              fieldHint: 'Choose the unit that matches the Duration number (days, weeks, months, etc.)',
              isArray: false,
              isGroup: false
            },
            data: this.durationUnits.map(u => u[1])
          }
        }
      },
      <GroupField>{
        generalProperties: {
          fieldApiName: 'doseAndRateDoseQuantity',
          fieldName: 'Dose Quantity',
          fieldLabel: 'Dose Quantity',
          fieldType: 'IndividualField',
          isArray: false,
          isGroup: true
        },
        keys: ['value', 'unit'],
        groupFields: {
          value: <IndividualField>{
            generalProperties: {
              fieldApiName: 'value',
              fieldName: 'Value',
              fieldLabel: 'Value',
              fieldType: 'IndividualField',
              inputType: 'number',
              isArray: false,
              isGroup: false
            },
            data: ''
          },
          unit: <IndividualField>{
            generalProperties: {
              fieldApiName: 'unit',
              fieldName: 'Unit',
              fieldLabel: 'Unit',
              fieldType: 'IndividualField',
              fieldPlaceholder: "e.g., mg, ml",
              inputType: 'text',
              isArray: false,
              isGroup: false
            },
            data: ''
          }
        }
      }
    ],
    'medicineRefOrMedicineCodeableConcept':
      <GroupField>{
        generalProperties: {
          fieldApiName: 'medicine',
          fieldLabel: 'Medicine',
          fieldName: 'Medicine',
          moreHint: "Choose the medicine to prescribe. If available in Pharmacy stock, select from 'Medication from Stock'; otherwise, search for a medicine not in stock.",
          fieldHint: "Choose the medicine to prescribe. If available in Pharmacy stock, select from 'Medication from Stock'; otherwise, search for a medicine not in stock.",
          groupFieldsHint: "Choose the medicine to prescribe. If available in Pharmacy stock, select from 'Medication from Stock'; otherwise, search for a medicine not in stock.",
          auth: {
            read: 'all',
            write: 'doctor, nurse'
          },
          fieldType: 'IndividualField',
          isArray: false,
          isHidden: true,
          isGroup: true
        },
        keys: ['where', 'medicationCodeableConcept', 'medicationReference'],
        groupFields: {
          // ...(g.MedInStock && g.MedInStock.entry && this.filterAvailableMedications(g.MedInStock.entry).length > 0 ? {
          ...(true ? {
            'where': {
              generalProperties: {
                fieldApiName: 'where',
                fieldName: 'Medicine Source',
                fieldLabel: 'Where should the patient get the medicine?',
                moreHint: "Select where the patient will obtain the medicine: from Pharmacy stock or not in stock.",
                fieldHint: "Choose 'From Stock' if the medicine is available in Pharmacy stock, otherwise choose 'Not In Stock'.",
                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
                fieldType: 'SingleCodeField',
                isArray: false,
                isGroup: false,
                controllingField: [
                  {
                    isAControlField: true,
                    dependentFieldVisibilityTriggerValue: 'Not In Stock',
                    controlledFieldDependencyId: 'medicationCodeableConcept'
                  },
                  {
                    isAControlField: true,
                    dependentFieldVisibilityTriggerValue: 'From Stock',
                    controlledFieldDependencyId: 'medicationReference'
                  }
                ]
              },
              data: [
                'From Stock',
                'Not In Stock'
              ],
            }
          } : {}),
          'medicationCodeableConcept': {
            generalProperties: {
              fieldApiName: 'medicationCodeableConcept',
              fieldName: 'Medicine Not In Stock',
              fieldLabel: 'Medicine Not In Stock',
              auth: {
                read: 'all',
                write: 'doctor, nurse'
              },
              allowedOthers: true,
              moreHint: "Search and choose a medication not in Pharmacy stock",
              fieldHint: "If the medicine is not in Pharmacy stock, select here",
              fieldType: 'CodeableConceptFieldFromBackEnd',
              isArray: false,
              isGroup: false,
              dependence_id: 'medicationCodeableConcept'
            },
            // data: g?.medication || []
            data: []
          },
          // ...(g.MedInStock && g.MedInStock.entry && this.filterAvailableMedications(g.MedInStock.entry).length > 0 ? {
          ...(true ? {
            'medicationReference': {
              generalProperties: {
                fieldApiName: 'medicationReference',
                fieldName: 'Medication from Stock',
                fieldLabel: 'Medication from Stock',
                moreHint: "Select medication from Pharmacy stock",
                fieldHint: "If the medicine is available in Pharmacy stock, select here",
                auth: {
                  read: 'all',
                  write: 'doctor, nurse'
                },
                fieldType: 'IndividualReferenceField',
                isArray: false,
                isGroup: false,
                dependence_id: 'medicationReference'
              },
              data: [],
              // data: g.MedInStock && g.MedInStock.entry
              //   ? this.filterAvailableMedications(g.MedInStock.entry).map((e: any) => {
              //     const stock = this.getMedicationTotalRemaining(e.resource);
              //     return {
              //       reference: 'Medication/' + e.resource.id,
              //       display: `${e.resource.code?.text || e.resource.code?.coding?.[0]?.display}`
              //     };
              //   })
              //   : []
            }
          } : {})

        }
      },
  };
  route = inject(ActivatedRoute);
  constructDosageTypeObject(val: any): Dosage | null {

    // TypeScript type check: constructDosageTypeObject returns Dosage with timing of type Dosage['timing']
    // Example:
    // const input = {
    //   dosageTiming: {
    //     duration: 7,
    //     durationUnit: 'day',
    //     timingCode: { code: 'BID', display: 'Two times a day', system: 'http://...' }
    //   }
    // };
    // const result = this.constructDosageTypeObject(input);
    // result.timing is of type Dosage['timing'] (per TypeScript type system).
    // You can hover over result.timing in an IDE and see its type is Dosage['timing'].
    // This is enforced by the type annotation in the constructDosageTypeObject implementation.

    const toReturn = {
      timing: <Dosage['timing']>{
        ...(val.dosageTiming?.durationUnit && val.dosageTiming?.duration ? {
          repeat: {
            duration: val.dosageTiming?.duration ? Number(val.dosageTiming.duration) : undefined,
            durationUnit: val.dosageTiming?.durationUnit ? this.durationUnitDisplayToCode(val.dosageTiming.durationUnit) : undefined,
          }
        } : {}),
        ...(val.dosageTiming?.timingCode ? {
          code: <CodeableConcept>{
            ...(val.dosageTiming?.timingCode?.code ? {
              coding: [{
                code: val.dosageTiming.timingCode.code,
                display: val.dosageTiming.timingCode.display,
                system: val.dosageTiming.timingCode.system
              }]
            } : {
              text: val.dosageTiming?.timingCode
            }),

          }
        } : {})

      },
      ...(val.dosageInstruction ? { text: val.dosageInstruction } : {}),
      ...((val.doseAndRateDoseQuantity?.value || val.doseAndRateDoseQuantity?.unit) ? {
        doseAndRate: [{
          doseQuantity: {
            value: val.doseAndRateDoseQuantity?.value ? Number(val.doseAndRateDoseQuantity.value) : undefined,
            unit: val.doseAndRateDoseQuantity?.unit ? val.doseAndRateDoseQuantity.unit : undefined
          }
        }]
      } : {})
    }
    //check if there are vaues;
    const validatedTReturn = {
      ...(toReturn.timing?.repeat && (toReturn.timing.repeat.duration || toReturn.timing.repeat.durationUnit) ? {
        timing: toReturn.timing
      } : {}),
      ...(toReturn.text ? { text: toReturn.text } : {}),
      ...(toReturn.doseAndRate ? { doseAndRate: toReturn.doseAndRate } : {})

    }

    if (validatedTReturn.timing) {
      validatedTReturn.timing = this.applyTimingCodeToRepeat(validatedTReturn.timing);
    }
    if (Object.keys(validatedTReturn).length === 0) {
      return null;
    }
    return validatedTReturn;

  }

  getPatientName(patientId: any): Observable<string | null> {
    return this.http.get("sample_fhir_patients.json").pipe(
      map((allArray: any) => {
        return allArray.find((element: any) => {
          return element?.identifier?.[0]?.value === patientId;
        });
      }),
      map((patient: any) => {
        // ✅ Add null check
        if (!patient?.name?.[0]) {
          return null;
        }
        return patient.name[0].given.join(' ') + ' ' + patient.name[0].family;
      }),
      catchError(() => of(null)) // ✅ Add error handling
    );
  }

  getPractitionerName(practitioner?: Practitioner | null): string {
    if (!practitioner) return '';
    const nameObj = practitioner.name?.[0];
    if (!nameObj) return '';
    const family = nameObj.family || '';
    const given = (nameObj.given || []).join(' ');
    const prefix = (nameObj.prefix || []).join(' ');
    return [prefix, given, family].filter(Boolean).join(' ');
  }

  getPatientIdFromRoute(): string | null {
    let startRoute = this.route.root.firstChild;
    let patientId = null;

    while (startRoute) {


      startRoute.snapshot.url.forEach((segment, index) => {
        console.log(startRoute?.routeConfig?.path?.includes("id"));
        if (segment.path.includes('patients') && startRoute?.routeConfig?.path?.includes("id")) {
          patientId = startRoute.snapshot.url[index + 1].path;

// $#$          // /* alert(patientId) */
        } else {

        }
      }
      )

      startRoute = startRoute?.firstChild
    }
    return patientId;
  }

  /**
   * Recursively removes fields with empty strings, null, undefined, 
   * empty arrays, or objects with no keys.
   */
  cleanResource(resource: any): any {
    if (Array.isArray(resource)) {
      return resource
        .map(v => this.cleanResource(v))
        .filter(v => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0) && !(typeof v === 'object' && Object.keys(v).length === 0));
    } else if (resource !== null && typeof resource === 'object' && !(resource instanceof Date)) {
      const cleaned: any = {};
      Object.keys(resource).forEach(key => {
        const value = this.cleanResource(resource[key]);
        if (value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0) && !(typeof value === 'object' && Object.keys(value).length === 0)) {
          cleaned[key] = value;
        }
      });
      return cleaned;
    }
    return resource;
  }

  convertFormFields(e: Map<string, {
    formFields: FormFields[],
    [key: string]: any
  }>): FormFields[] {
    const fieldsToReturn: FormFields[] = [];
    Array.from(e).forEach((f) => {
      f[1].formFields.forEach((g) => {
        fieldsToReturn.push(g);
      })
    })
    return fieldsToReturn
  }

  searchCodeableConceptByDisplayOrText(concept: any, codOrText: string, system?: string): boolean {
    if (!concept) {
      return false;
    }
    if (!concept.coding && !concept.text) {
      return false;
    }
    if (concept.coding && !concept.text && !Array.isArray(concept.coding)) {
      return false;
    }
    if (concept.text && concept.text.toLowerCase() === codOrText.toLowerCase()) {
// $#$      // /* alert(concept.text) */
      return true;
    }
    if (concept.coding && Array.isArray(concept.coding)) {
      return concept.coding.some((coding: any) => {

        if (coding.display && coding.display.toLowerCase() === codOrText.toLowerCase()) {
          return true;
        }
        return false;
      });
    }
    return false;
  }

  convertAllValueTypesToString(obj: Observation | any): string {
    if (!obj || typeof obj !== 'object') return '';

    const toStr = (v: unknown) => (v ?? '').toString().trim();

    const fmtQuantity = (q: any) => {
      if (!q) return '';
      const comp = toStr(q.comparator);
      const val = toStr(q.value);
      const unit = toStr(q.unit || q.code);
      return [comp + val, unit].filter(Boolean).join(' ').trim();
    };

    const fmtCodeableConcept = (cc: any) => {
      if (!cc) return '';
      if (cc.text) return toStr(cc.text);
      if (Array.isArray(cc.coding)) {
        const parts = cc.coding
          .map((c: any) => toStr(c.display || c.code || ''))
          .filter(Boolean);
        if (parts.length) return Array.from(new Set(parts)).join(', ');
      }
      return '';
    };

    const fmtRange = (r: any) => {
      if (!r) return '';
      const low = fmtQuantity(r.low);
      const high = fmtQuantity(r.high);
      if (low && high) return `${low} - ${high}`;
      return low || high || '';
    };

    const fmtRatio = (ra: any) => {
      if (!ra) return '';
      const num = fmtQuantity(ra.numerator);
      const den = fmtQuantity(ra.denominator);
      if (num && den) return `${num} / ${den}`;
      return num || den || '';
    };

    const fmtSampledData = (sd: any) => {
      if (!sd) return '';
      // Use raw data string; avoid overlong output by trimming
      const data = toStr(sd.data);
      return data;
    };

    const fmtPeriod = (p: any) => {
      if (!p) return '';
      const start = toStr(p.start);
      const end = toStr(p.end);
      if (start && end) return `${start} - ${end}`;
      return start || end || '';
    };

    const valueProps = [
      'valueQuantity',
      'valueCodeableConcept',
      'valueString',
      'valueBoolean',
      'valueInteger',
      'valueRange',
      'valueRatio',
      'valueSampledData',
      'valueTime',
      'valueDateTime',
      'valuePeriod'
    ] as const;

    for (const prop of valueProps) {
      const v = obj[prop as keyof typeof obj];
      if (v === undefined || v === null) continue;

      switch (prop) {
        case 'valueQuantity':
          return fmtQuantity(v);
        case 'valueCodeableConcept':
          return fmtCodeableConcept(v);
        case 'valueString':
        case 'valueTime':
        case 'valueDateTime':
          return toStr(v);
        case 'valueBoolean':
          return String(Boolean(v));
        case 'valueInteger':
          return typeof v === 'number' ? String(v) : toStr(v);
        case 'valueRange':
          return fmtRange(v);
        case 'valueRatio':
          return fmtRatio(v);
        case 'valueSampledData':
          return fmtSampledData(v);
        case 'valuePeriod':
          return fmtPeriod(v);
        default:
          break;
      }
    }

    if (Array.isArray(obj.component) && obj.component.length > 0) {
      const compStrs = obj.component.map((c: any) => {
        const cName = fmtCodeableConcept(c.code);
        const cVal = this.convertAllValueTypesToString(c);
        let text = '';
        if (cName && cVal) text = `${cName}: ${cVal}`;
        else text = cVal || cName;

        let rangeStr = '';
        if (Array.isArray(c.referenceRange)) {
          const ranges = c.referenceRange.map((r: any) => {
            const bounds = [];
            if (r.low?.value != null) bounds.push(r.low.value);
            if (r.high?.value != null) bounds.push(r.high.value);
            return bounds.length ? `[${bounds.join(' - ')}]` : '';
          }).filter(Boolean);
          if (ranges.length) {
            rangeStr = ranges.join(', ');
          }
        }

        let interpStr = '';
        if (Array.isArray(c.interpretation) && c.interpretation.length > 0) {
          interpStr = fmtCodeableConcept(c.interpretation[0]);
        }

        let out = text;
        if (rangeStr) out += ` ${rangeStr}`;
        if (interpStr) out += ` - ${interpStr}`;
        return out;
      }).filter(Boolean);
      if (compStrs.length) return compStrs.join(', ');
    }

    return '';
  }


  chooseFirstStringFromCodeableConcept(concept: any): string {
    if (!concept) {
      return '';
    }
    if (concept.text) {
      return concept.text;
    }
    if (concept.coding && Array.isArray(concept.coding) && concept.coding.length > 0) {
      let index = 0;
      let textToReturn = '';
      while (index < concept.coding.length && !textToReturn) {
        if (concept.coding[index].display) {
          textToReturn = concept.coding[index].display;
          // return textToReturn;
        }
        index++;
      }
      return textToReturn
    }
    return '';
  }

  getBefittingIconsForVitals(vitalCode: string): string {
    const key = (vitalCode || '').trim().toLowerCase();

    // Heuristic matches
    if (key.includes('blood pressure') || key.includes('systolic') || key.includes('diastolic') || key === 'bp') {
      return 'speed';
    }
    if (key.includes('oxygen') || key.includes('spo2')) {
      return 'water_drop';
    }
    if (key.includes('respir')) {
      return 'air';
    }
    if (key.includes('pulse')) {
      return 'favorite';
    }
    if (key.includes('temp')) {
      return 'thermostat';
    }
    if (key.includes('weight')) {
      return 'monitor_weight';
    }
    if (key.includes('height') || key.includes('stature')) {
      return 'height';
    }
    if (key.includes('bmi') || key.includes('body mass index')) {
      return 'insights';
    }
    if (key.includes('glucose') || key.includes('sugar')) {
      return 'science';
    }
    if (key.includes('pain')) {
      return 'sentiment_dissatisfied';
    }

    // Exact/synonym matches
    switch (key) {
      // using text not codes
      case 'body temperature':
      case 'temperature':
        return 'thermostat';
      case 'heart rate':
        return 'favorite';
      case 'respiratory rate':
      case 'respiration rate':
        return 'air';
      case 'oxygen saturation':
        return 'water_drop';
      case 'body height':
      case 'height':
      case 'stature':
        return 'height';
      case 'body weight':
      case 'weight':
        return 'monitor_weight';
      default:
        return 'insights';
    }
  }

  FilterResourcesBasedOnReferenceFieldId<T>(resources: Resource[], referenceField: string, referenceId: string): Resource[] {
    return resources.filter((resource: any) => {
      const refFieldValue = resource[referenceField];
      if (typeof refFieldValue === 'string') {
        return refFieldValue === referenceId;
      } else if (refFieldValue && typeof refFieldValue === 'object' && 'reference' in refFieldValue) {
        return (refFieldValue as any).reference === referenceId;
      }
      return false;
    });

  }

  /**
   * Convert a flat values object gathered from a form into a FHIR resource structure.
   * Heuristics:
   * - Strings containing "$#$" => CodeableConcept (code$#$display$#$system).
   * - Keys containing "status" => resource.status (lowercased).
   * - Keys ending with Date / DateTime => ISO instant (onsetDateTime, effectiveDateTime, authoredOn, etc. if present).
   * - Provided patientId => subject reference for common clinical resources (Observation, Condition, MedicationRequest, ServiceRequest).
   * - Provided encounterId => encounter reference for Observation / Condition / MedicationRequest.
   * - performerId => performer reference array when applicable.
   * - codeFieldNames / referenceFieldNames override auto-detection for special fields.
   * - Attachments (array of { name, type, data(base64) }) -> resource.attachment(s) or valueAttachment if single.
   */
  buildFhirResource(
    resourceType: string,
    values: Record<string, any>,
    opts?: {
      patientId?: string;
      encounterId?: string;
      performerId?: string;
      defaultStatus?: string;
      codeFieldNames?: string[];
      referenceFieldNames?: string[];
    }
  ): any {
    const out: any = { resourceType };

    // Subject / patient reference
    if (opts?.patientId && resourceType !== 'Patient') {
      const subjectCapable = ['Observation', 'Condition', 'MedicationRequest', 'ServiceRequest', 'AllergyIntolerance', 'Procedure'];
      if (subjectCapable.includes(resourceType)) {
        out.subject = { reference: `Patient/${opts.patientId}` };
      }
    }

    // Encounter reference
    if (opts?.encounterId) {
      const encounterCapable = ['Observation', 'Condition', 'MedicationRequest', 'Procedure'];
      if (encounterCapable.includes(resourceType)) {
        out.encounter = { reference: `Encounter/${opts.encounterId}` };
      }
    }

    // Performer
    if (opts?.performerId) {
      const performerCapable = ['Observation', 'MedicationRequest', 'Procedure', 'ServiceRequest'];
      if (performerCapable.includes(resourceType)) {
        out.performer = [{ reference: `Practitioner/${opts.performerId}` }];
      }
    }

    const codeFields = new Set(opts?.codeFieldNames || []);
    const refFields = new Set(opts?.referenceFieldNames || []);

    const isEmpty = (v: any) =>
      v === undefined || v === null ||
      (typeof v === 'string' && v.trim() === '') ||
      (Array.isArray(v) && v.length === 0);

    for (const key of Object.keys(values || {})) {
      let val = values[key];
      if (isEmpty(val)) continue;

      // Status
      if (key.toLowerCase().includes('status') && !out.status && typeof val === 'string') {
        out.status = val.toLowerCase();
        continue;
      }

      // Date/time normalization
      if (/date(time)?$/i.test(key) && typeof val === 'string') {
        const parsed = new Date(val);
        if (!isNaN(parsed.getTime())) {
          // Map onsetDateTime vs generic dateTime
          out[key] = parsed.toISOString();
          continue;
        }
      }

      // Explicit reference mapping (string or array)
      if (refFields.has(key) || /reference|subject|encounter|patient|performer/i.test(key)) {
        out[key] = this.mapReferenceValue(val);
        continue;
      }

      // CodeableConcept mapping
      if (codeFields.has(key) || this.looksLikeCode(val, key)) {
// $#$        // /* alert('codeable concept:' + key + ' val:' + val); */
        out[key] = this.toCodeableConcept(val);
        continue;
      }

      // Attachments
      if (key.toLowerCase().includes('attachment')) {
        out[key] = this.mapAttachments(val);
        continue;
      }

      if (key.toLowerCase().trim() == 'resourcetype') {
        out[key] = val;
      }

      // Simple pass-through
      out[key] = val;
    }

    // Default status if not set
    if (opts?.defaultStatus && !out.status) {
      out.status = opts.defaultStatus;
    }
// $#$    // /* alert(JSON.stringify(out) */);
    return out;
  }

  seperateTotalPriceComponents(charge: ChargeItemDefinition, includeCurrency: boolean = false): any {
    const propertyGroups = charge.propertyGroup || [];
    if (propertyGroups.length === 0) {
      return null;
    }
    const toReturnComponents: { [key: string]: any } = {
      base: 0,
      surcharge: 0,
      deduction: 0,
      discount: 0,
      tax: 0,
      informational: 0,
      totalSellingPrice: 0,
      currency: 'NGN'
    };

    // Aggregate from ALL property groups
    propertyGroups.forEach(pg => {
      const eachPriceComponent = pg.priceComponent || [];
      eachPriceComponent.forEach(pc => {
        const type = (pc.type || '').toString().trim().toLowerCase();
        const value = Number(pc.amount?.value) || 0;

        // Extract currency from the first component that has it
        if (pc.amount?.currency) {
          toReturnComponents['currency'] = pc.amount.currency;
        }

        switch (type) {
          case 'base':
            toReturnComponents['base'] += value;
            break;
          case 'surcharge':
            toReturnComponents['surcharge'] += value;
            break;
          case 'deduction':
            toReturnComponents['deduction'] += value;
            break;
          case 'discount':
            toReturnComponents['discount'] += value;
            break;
          case 'tax':
            toReturnComponents['tax'] += value;
            break;
          case 'informational':
          case 'information':
            toReturnComponents['informational'] += value;
            break;
        }
      });
    });

    // Explicitly calculate total selling price
    // Total = (Base + Surcharge + Tax) - (Deduction + Discount)
    // We use Math.abs to ensure that even if the server sent negative values for deductions, 
    // they are subtracted from the total.
    toReturnComponents['totalSellingPrice'] =
      (toReturnComponents['base'] || 0) +
      (toReturnComponents['surcharge'] || 0) +
      (toReturnComponents['tax'] || 0) -
      Math.abs(toReturnComponents['deduction'] || 0) -
      Math.abs(toReturnComponents['discount'] || 0);

    if (includeCurrency) {
      const currency = toReturnComponents['currency'];
      const formatter = new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2
      });
      Object.keys(toReturnComponents).forEach(key => {
        if (typeof toReturnComponents[key] === 'number' && key !== 'currency') {
          toReturnComponents[key] = formatter.format(toReturnComponents[key]);
        }
      });
    }
    return toReturnComponents;
  }

  /**
   * Get currency symbol for a given currency code
   */
  getCurrencySymbol(currencyCode: string): string {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: currencyCode || 'NGN',
    }).format(0).replace(/\d|\.|\s/g, '');
  }

  // ---- Helpers ----

  private looksLikeCode(val: any, key: string): boolean {
    if (typeof val === 'string' && val.includes('$#$')) return true;
    if (Array.isArray(val) && val.every(v => typeof v === 'string' && v.includes('$#$'))) return true;
    // Exclude 'resourceType' from being treated as a code field
    if (key.toLowerCase() === 'resourcetype') return false;
    return /(code|category|priority|severity|class|type)$/i.test(key);
  }

  public toCodeableConcept(input: any): any {
    if (!input) return null;

    if (typeof input === 'string') {
      if (input.includes('$#$')) {
        const [code = '', display = '', system = ''] = input.split('$#$');
        return { coding: [{ code, display, system }], text: display || code };
      }
      return { text: input };
    }

    if (Array.isArray(input)) {
      // array of encoded strings or objects
      const coding = input.flatMap((v: any): any => {
        if (typeof v === 'string' && v.includes('$#$')) {
          const [code = '', display = '', system = ''] = v.split('$#$');
          return [{ code, display, system }];
        }
        if (v && typeof v === 'object' && (v.code || v.display)) {
          return [{ code: v.code || '', display: v.display || v.text || '', system: v.system || '' }];
        }
         if (v && typeof v === 'object' && (v.coding && Array.isArray(v.coding))) {
          return [{...(v.text ? { text: v.text } : {}),

           coding: [{ code: v.coding[0].code || '', display: v.coding[0].display || v.coding[0].text || '', system: v.coding[0].system || ''}]}
        ]}
        return [];
      });
      // const returnObj = {} as any;
      if(typeof (coding[0]) === 'object' && coding[0].coding && Array.isArray(coding[0].coding) && coding[0].coding.length > 0) {
      return { coding:coding[0].coding, text: coding[0].text || coding[0].display || coding[0].code };
      }
       
      
     return coding.length ? { coding, text: coding[0].text || coding[0].display || coding[0].code } : { text: String(input) };
    
    }
    if (typeof input === 'object') {
      if (Array.isArray(input.coding) ) return input;
      if (input.code || input.display) {
        return {
          coding: [{ code: input.code || '', display: input.display || input.text || '', system: input.system || '' }],
          text: input.display || input.code
        };
        // if it is alread a codeable concept 

        

      } else {
        if (input.text) {
          return { text: input.text };
        }
      }

    }
    return { text: String(input) };
  }

  /**
   * Reverse of {@link toCodeableConcept}: converts a FHIR CodeableConcept/Coding-ish input into a string.
   *
   * Behavior (best-effort):
   * - If a coding is present, returns an encoded string: `code$#$display$#$system`.
   * - If only text is present, returns `text`.
   * - Always returns a string (never throws).
   */
  public codeableConceptToString(input: unknown): string {
    const toStr = (v: unknown) => (v ?? '').toString().trim();

    if (input === null || input === undefined) return '';
    if (typeof input === 'string') return input.trim();

    // Sometimes callers pass an array of encoded strings / codings.
    if (Array.isArray(input)) {
      for (const item of input) {
        const s = this.codeableConceptToString(item);
        if (s) return s;
      }
      return '';
    }

    if (typeof input !== 'object') {
      return toStr(input);
    }

    const anyObj = input as any;
    const text = toStr(anyObj.text);

    const pickCoding = (codingArray: any[]): any | null => {
      if (!Array.isArray(codingArray) || codingArray.length === 0) return null;
      const normalized = codingArray
        .filter((c) => c && typeof c === 'object')
        .map((c) => ({
          code: toStr(c.code),
          display: toStr(c.display),
          system: toStr(c.system)
        }))
        .filter((c) => c.code || c.display || c.system);
      if (!normalized.length) return null;

      // Prefer codings with system+code, then code, then display.
      return (
        normalized.find((c) => !!c.system && !!c.code) ||
        normalized.find((c) => !!c.code) ||
        normalized.find((c) => !!c.display) ||
        normalized[0]
      );
    };

    const coding = pickCoding(anyObj.coding);

    // Accept Coding-like objects directly (e.g., { code, display, system }).
    const directCoding = !coding && (anyObj.code || anyObj.display || anyObj.system)
      ? {
        code: toStr(anyObj.code),
        display: toStr(anyObj.display),
        system: toStr(anyObj.system)
      }
      : null;

    const chosen = coding || directCoding;
    if (chosen) {
      const code = toStr(chosen.code);
      const display = toStr(chosen.display) || text || code;
      const system = toStr(chosen.system) || ((code || display) ? 'urn:unknown' : '');

      // If we can't form a meaningful encoded string, fallback to a label.
      if (!code && !display && !system) return text;
      if (!code && !display && system) return system;

      return `${code}$#$${display}$#$${system}`;
    }

    return text;
  }

  /** Converts a FHIR Quantity into a readable string like `12.5 mg` (best-effort). */
  public quantityToString(quantity: Quantity | any): string {
    const toStr = (v: unknown) => (v ?? '').toString().trim();

    if (quantity === null || quantity === undefined) return '';
    if (typeof quantity === 'string') return quantity.trim();
    if (typeof quantity !== 'object') return toStr(quantity);

    const comp = toStr(quantity.comparator);
    const valueRaw = (quantity as any).value;
    const value = (typeof valueRaw === 'number' && Number.isFinite(valueRaw))
      ? String(valueRaw)
      : toStr(valueRaw);
    const unit = toStr((quantity as any).unit || (quantity as any).code);

    const left = `${comp}${value}`.trim();
    return [left, unit].filter(Boolean).join(' ').trim();
  }

  /**
   * Three-tiered querying strategy to reduce reliance on flaky/limited servers.
   *
   * Tier 1: Fetch from backend with query params. On success, update StateService via tap.
   * Tier 2: If Tier 1 fails, try returning from StateService (orgWide or patient scoped). If empty,
   * Tier 3: Fetch from backend without query params, update state via tap, then keep streaming the state.
   *
   * Notes:
   * - Returned Observable is state-backed (no take(1)), so later state updates also re-emit.
   * - Local filtering is re-applied (deep keys, arrays, CodeableConcept/Reference/Coding).
   * - OperationOutcome responses are treated as errors and are never emitted.
   */
  threeTierQueryAndSyncToState$<T extends FhirResource = FhirResource>(args: ThreeTierFhirQuery): Observable<T[]> {
    return defer(() => {
      const resourceType = (args?.resourceType || '').toString().trim();
      if (!resourceType) return of([] as T[]);
      const ctx = args.context;

      const urlSearch = (args.urlSearchObject ?? args.queryParams ?? {}) as Record<string, any>;
      const localFilters: Array<{ key: string; value: any }> = [];
      if (Array.isArray(args.querySearchObject)) {
        localFilters.push(...args.querySearchObject.filter((f) => !!f?.key));
      } else if (args.querySearchObject && typeof args.querySearchObject === 'object' && (args.querySearchObject as any).key) {

        localFilters.push(args.querySearchObject as any);
      }
      if (args.filterKey) {
        localFilters.push({ key: args.filterKey, value: args.filterValue });
      }

      const useCurrentPatientId = args.useCurrentPatientId && args.useCurrentPatientId === true;
      const resolvedPatientId = (args.patientId ?? (useCurrentPatientId ? this.stateService.currentPatientId$.getValue() : null)) || null;

      // Enforce: if caller asks for patient scope OR asks to use current patient id, we must resolve a patient id.
      if ((args.scope === 'patient' || useCurrentPatientId) && !resolvedPatientId) {
        return throwError(() => new Error(
          `threeTierQueryAndSyncToState$ requires a patientId when scope='patient' or useCurrentPatientId=true (resourceType=${resourceType}).`
        ));
      }

      const scope: 'patient' | 'orgWide' =
        args.scope === 'patient' ? 'patient' :
          args.scope === 'orgWide' ? 'orgWide' :
            resolvedPatientId ? 'patient' : 'orgWide';

      const stateSubject = this.getStateSubjectForResourceType(resourceType, scope);
      if (!stateSubject) {
        // Fallback: no state backing for this resource type; just do a single fetch (best-effort).
        const url = this.buildFhirSearchUrlWithOr(resourceType, urlSearch, args.count, args.format, args.urlOrKeys);
        return this.http.get<any>(url, ctx ? { context: ctx } : {}).pipe(
          map((bundle) => this.extractResourcesFromBundle(bundle).filter((r) => r?.resourceType !== 'OperationOutcome') as T[]),
          map((resources) => this.applyLocalFilter(resources, args.filterKey, args.filterValue) as T[]),
          catchError(() => of([] as T[]))
        );
      }

      const stateFiltered$ = stateSubject.asObservable().pipe(
        map((wrappers: any[]) => this.unwrapStateWrappers(wrappers) as T[]),
        map((resources: T[]) => this.applyLocalFilters(resources, localFilters) as T[])
      );

      const defaultMaxPages = typeof this.fhirMaxAllAtOnceCount === 'number' && this.fhirMaxAllAtOnceCount > 0 ? this.fhirMaxAllAtOnceCount : 10;
      const expectedMinLength = typeof args.expectedMinLength === 'number'
        ? Math.max(0, Math.floor(args.expectedMinLength))
        : 0;

      const tier3TriggerWhenBelowExpectedMinOnTier1Failure =
        args.tier3TriggerWhenBelowExpectedMinOnTier1Failure === true && expectedMinLength > 0;

      // Tier-1 (server query) observable: emit *filtered* results to caller for speed,
      // but still upsert the *full* bundle into StateService (dedupe happens there).
      const queryFailed$ = new BehaviorSubject<boolean>(false);
      const effectiveCount = typeof args.count === 'number' && args.count > 0 ? args.count : this.fhirDefaultCount;
      const queryUrl = this.buildFhirSearchUrlWithOr(resourceType, urlSearch, effectiveCount, args.format, args.urlOrKeys);

      const tier1FollowNextLinks = args.tier1FollowNextLinks === true;
      const tier1MaxPages = typeof args.tier1MaxPages === 'number' && args.tier1MaxPages > 0
        ? Math.floor(args.tier1MaxPages)
        : defaultMaxPages;

      const tier1Bundles$ = (tier1FollowNextLinks
        ? this.fetchBundlesFollowingNextLinksUntilStream$(queryUrl, tier1MaxPages, localFilters, expectedMinLength, ctx)
        : this.http.get<any>(queryUrl, ctx ? { context: ctx } : {})
      ).pipe(
        tap((bundle) => {
          if (this.isOperationOutcomeBundle(bundle)) {
            throw new Error('OperationOutcome received instead of expected Bundle entries');
          }
          this.updateStateFromBundle(bundle, scope);
        }),
        catchError((err) => {
          queryFailed$.next(true);
          console.warn('Tier-1 query fetch failed for', resourceType, err);
          return EMPTY;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

      // Emit only the first page's filtered results for responsiveness.
      // When tier-1 paging is enabled, subsequent pages still upsert into state,
      // and callers will observe growth via the merged `stateFiltered$` stream.
      const tier1Emit$ = tier1Bundles$.pipe(
        take(1),
        map((bundle) => this.extractResourcesFromBundle(bundle).filter((r) => r?.resourceType !== 'OperationOutcome') as T[]),
        map((resources) => this.applyLocalFilters(resources, localFilters) as T[])
      );

      // Keep the tier-1 paging subscription alive until completion.
      const tier1Drain$ = tier1Bundles$.pipe(ignoreElements());

      // Tier-3 (full fetch) only triggers once if tier-1 failed AND filtered state remains empty.
      const patientScopeSearchParam = (args.patientScopeSearchParam || 'subject').toString().trim() || 'subject';
      const tier3UrlSearch: Record<string, FhirSearchParamValue> = {};
      if (scope === 'patient' && resolvedPatientId) {
        if (patientScopeSearchParam === 'patient') tier3UrlSearch[patientScopeSearchParam] = resolvedPatientId;
        else tier3UrlSearch[patientScopeSearchParam] = `Patient/${resolvedPatientId}`;
      }

      const allUrl = this.buildFhirSearchUrlWithOr(resourceType, tier3UrlSearch, effectiveCount, args.format, args.urlOrKeys);
      const tier3FollowNextLinks = args.tier3FollowNextLinks === true;
      const tier3MaxPages = typeof args.tier3MaxPages === 'number' && args.tier3MaxPages > 0 ? Math.floor(args.tier3MaxPages) : defaultMaxPages;

      const tier3Trigger$ = combineLatest([queryFailed$, stateFiltered$]).pipe(
        filter(([failed, current]) => {
          if (!failed) return false;
          const list = Array.isArray(current) ? current : [];
          if (tier3TriggerWhenBelowExpectedMinOnTier1Failure) return list.length < expectedMinLength;
          return list.length === 0;
        }),
        take(1),
        switchMap(() => {
          const pageBundles$ = tier3FollowNextLinks
            ? this.fetchBundlesFollowingNextLinksUntil$(allUrl, tier3MaxPages, localFilters, expectedMinLength, ctx)
            : this.http.get<any>(allUrl, ctx ? { context: ctx } : {}).pipe(map((b) => [b]));

          return pageBundles$.pipe(
            switchMap((bundles: any[]) => of(...bundles)),
            tap((bundle) => {
              if (this.isOperationOutcomeBundle(bundle)) {
                throw new Error('OperationOutcome received instead of expected Bundle entries');
              }
              this.updateStateFromBundle(bundle, scope);
            }),
            catchError((err) => {
              console.warn('Tier-3 full fetch failed for', resourceType, err);
              // For patient scope, fail loudly (caller must handle). For org-wide, stay resilient.
              return scope === 'patient'
                ? throwError(() => err)
                : EMPTY;
            }),
            ignoreElements()
          );
        })
      );

      // Return: tier-1 fast results + continuously filtered state stream.
      return merge(tier1Emit$, tier1Drain$, tier3Trigger$, stateFiltered$);
    });
  }

  /**
   * One-shot variant of `threeTierQueryAndSyncToState$`.
   *
   * Use this when you need a snapshot list that completes (e.g., validations),
   * and you do NOT want a long-lived state-backed stream.
   */
  threeTierQueryAndSyncToStateOnce$<T extends FhirResource = FhirResource>(args: ThreeTierFhirQuery): Observable<T[]> {
    return defer(() => {
      const resourceType = (args?.resourceType || '').toString().trim();
      if (!resourceType) return of([] as T[]);

      const urlSearch = (args.urlSearchObject ?? args.queryParams ?? {}) as Record<string, any>;
      const localFilters: Array<{ key: string; value: any }> = [];
      if (Array.isArray(args.querySearchObject)) {
        localFilters.push(...args.querySearchObject.filter((f) => !!f?.key));
      } else if (args.querySearchObject && typeof args.querySearchObject === 'object' && (args.querySearchObject as any).key) {
        localFilters.push(args.querySearchObject as any);
      }
      if (args.filterKey) {
        localFilters.push({ key: args.filterKey, value: args.filterValue });
      }

      const useCurrentPatientId = args.useCurrentPatientId && args.useCurrentPatientId === true;
      const resolvedPatientId = (args.patientId ?? (useCurrentPatientId ? this.stateService.currentPatientId$.getValue() : null)) || null;

      // Enforce: if caller asks for patient scope OR asks to use current patient id, we must resolve a patient id.
      if ((args.scope === 'patient' || useCurrentPatientId) && !resolvedPatientId) {
        return throwError(() => new Error(
          `threeTierQueryAndSyncToStateOnce$ requires a patientId when scope='patient' or useCurrentPatientId=true (resourceType=${resourceType}).`
        ));
      }

      const scope: 'patient' | 'orgWide' =
        args.scope === 'patient' ? 'patient' :
          args.scope === 'orgWide' ? 'orgWide' :
            resolvedPatientId ? 'patient' : 'orgWide';

      const stateSubject = this.getStateSubjectForResourceType(resourceType, scope);
      const effectiveCount = typeof args.count === 'number' && args.count > 0 ? args.count : this.fhirDefaultCount;
      const queryUrl = this.buildFhirSearchUrlWithOr(resourceType, urlSearch, effectiveCount, args.format, args.urlOrKeys);

      const defaultMaxPages = typeof this.fhirMaxAllAtOnceCount === 'number' && this.fhirMaxAllAtOnceCount > 0 ? this.fhirMaxAllAtOnceCount : 10;
      const expectedMinLength = typeof args.expectedMinLength === 'number'
        ? Math.max(0, Math.floor(args.expectedMinLength))
        : 0;

      const tier3TriggerWhenBelowExpectedMinOnTier1Failure =
        args.tier3TriggerWhenBelowExpectedMinOnTier1Failure === true && expectedMinLength > 0;

      // No state backing: just do a single fetch.
      if (!stateSubject) {
        const ctx = args.context;
        return this.http.get<any>(queryUrl, ctx ? { context: ctx } : {}).pipe(
          map((bundle) => this.extractResourcesFromBundle(bundle).filter((r) => r?.resourceType !== 'OperationOutcome') as T[]),
          map((resources) => this.applyLocalFilters(resources, localFilters) as T[]),
          catchError(() => of([] as T[]))
        );
      }

      const stateSnapshot$ = stateSubject.asObservable().pipe(
        take(1),
        map((wrappers: any[]) => this.unwrapStateWrappers(wrappers) as T[]),
        map((resources: T[]) => this.applyLocalFilters(resources, localFilters) as T[])
      );

      const tier1FollowNextLinks = args.tier1FollowNextLinks === true;
      const tier1MaxPages = typeof args.tier1MaxPages === 'number' && args.tier1MaxPages > 0
        ? Math.floor(args.tier1MaxPages)
        : defaultMaxPages;

      const ctx = args.context;

      const tier1Paged$ = (tier1FollowNextLinks
        ? this.fetchBundlesFollowingNextLinksUntil$(queryUrl, tier1MaxPages, localFilters, expectedMinLength, ctx)
        : this.http.get<any>(queryUrl, ctx ? { context: ctx } : {}).pipe(map((b) => [b]))
      ).pipe(
        switchMap((bundles: any[]) => {
          const aggregated: T[] = [];
          for (const bundle of (bundles || [])) {
            const resources = this.handleBundleForOneShotFlow<T>(bundle, scope);
            aggregated.push(...resources);
          }
          return of(aggregated);
        }),
        map((resources: T[]) => this.applyLocalFilters(resources, localFilters) as T[]),
        catchError(() => of(null as unknown as T[]))
      );

      const tier1$ = tier1Paged$;

      const patientScopeSearchParam = (args.patientScopeSearchParam || 'subject').toString().trim() || 'subject';
      const tier3UrlSearch: Record<string, FhirSearchParamValue> = {};
      if (scope === 'patient' && resolvedPatientId) {
        if (patientScopeSearchParam === 'patient') tier3UrlSearch[patientScopeSearchParam] = resolvedPatientId;
        else tier3UrlSearch[patientScopeSearchParam] = `Patient/${resolvedPatientId}`;
      }

      const allUrl = this.buildFhirSearchUrlWithOr(resourceType, tier3UrlSearch, effectiveCount, args.format, args.urlOrKeys);
      const tier3FollowNextLinks = args.tier3FollowNextLinks === true;
      const tier3MaxPages = typeof args.tier3MaxPages === 'number' && args.tier3MaxPages > 0 ? Math.floor(args.tier3MaxPages) : defaultMaxPages;

      const tier3$ = (tier3FollowNextLinks
        ? this.fetchBundlesFollowingNextLinksUntil$(allUrl, tier3MaxPages, localFilters, expectedMinLength, ctx).pipe(
          switchMap((bundles: any[]) => of(...bundles))
        )
        : this.http.get<any>(allUrl, ctx ? { context: ctx } : {})
      ).pipe(
        map((bundle) => this.handleBundleForOneShotFlow<T>(bundle, scope)),
        reduce((acc: T[], page: T[]) => acc.concat(page), [] as T[]),
        map((resources) => this.applyLocalFilters(resources, localFilters) as T[]),
        catchError((err) => scope === 'patient' ? throwError(() => err) : of([] as T[]))
      );

      return tier1$.pipe(
        switchMap((tier1List: T[] | null) => {
          console.log(tier1List +
            "tier1list tier1 list tier1 list tier1 list tier1 list tier1 list tier1 list tier1 list tier1 list tier1 list tier1 list"
          );
          if (Array.isArray(tier1List)) return of(tier1List);
          return stateSnapshot$.pipe(
            switchMap((snap: T[]) => {
              const list = Array.isArray(snap) ? snap : [];
              if (tier3TriggerWhenBelowExpectedMinOnTier1Failure) {
                return list.length >= expectedMinLength ? of(list) : tier3$;
              }
              return list.length ? of(list) : tier3$;
            })
          );
        })
      );
    });
  }

  /**
   * Streams pages by following `Bundle.link[relation=next]` up to `maxPages`.
   * Stops early when locally-filtered results reach `expectedMinLength`.
   */
  private fetchBundlesFollowingNextLinksUntilStream$(
    startUrl: string,
    maxPages: number,
    localFilters: Array<{ key: string; value: any }>,
    expectedMinLength: number,
    context?: HttpContext
  ): Observable<any> {
    const firstUrl = (startUrl || '').toString().trim();
    if (!firstUrl) return EMPTY;

    const cap = typeof maxPages === 'number' && maxPages > 0 ? Math.floor(maxPages) : 10;
    const expected = typeof expectedMinLength === 'number' && expectedMinLength > 0 ? Math.floor(expectedMinLength) : 0;

    type PageState = { bundle: any; page: number; accFilteredLen: number };
    const bundleFilteredLen = (bundle: any) => {
      const resources = this.extractResourcesFromBundle(bundle);
      const filtered = this.applyLocalFilters(resources, localFilters);
      return Array.isArray(filtered) ? filtered.length : 0;
    };

    return this.http.get<any>(firstUrl, context ? { context } : {}).pipe(
      map((bundle) => ({ bundle, page: 1, accFilteredLen: bundleFilteredLen(bundle) } as PageState)),
      expand((state) => {
        if (expected > 0 && state.accFilteredLen >= expected) return EMPTY;
        if (state.page >= cap) return EMPTY;
        const nextUrl = this.normalizeNextLinkUrl(this.findNextLinkUrl(state.bundle));
        if (!nextUrl) return EMPTY;
        return this.http.get<any>(nextUrl, context ? { context } : {}).pipe(
          map((bundle) => ({
            bundle,
            page: state.page + 1,
            accFilteredLen: state.accFilteredLen + bundleFilteredLen(bundle)
          } as PageState))
        );
      }),
      take(cap),
      map((s) => s.bundle)
    );
  }

  private handleBundleForOneShotFlow<T extends FhirResource = FhirResource>(
    bundle: any,
    scope: 'patient' | 'orgWide'
  ): T[] {
    const resources = this.extractResourcesFromBundle(bundle) as T[];
    this.updateStateFromBundle(bundle, scope);
    return resources;
  }

  private updateStateFromBundle(bundle: any, scope: 'patient' | 'orgWide') {
    // StateService methods accept Bundle; in practice many callers pass `any`.
    if (!bundle || typeof bundle !== 'object') return;
    if (scope === 'patient') this.stateService.processBundleTransaction(bundle as any);
    else this.stateService.processOrgWideBundleTransaction(bundle as any);
  }

  private isOperationOutcomeBundle(bundle: any): boolean {
    const entries = Array.isArray(bundle?.entry) ? bundle.entry : [];
    return entries.some((e: any) => e?.resource?.resourceType === 'OperationOutcome');
  }


  private extractResourcesFromBundle(bundle: any): any[] {
    if (this.isOperationOutcomeBundle(bundle)) {
      throw new Error('OperationOutcome received instead of expected Bundle entries');
    }

    const entries = Array.isArray(bundle?.entry) ? bundle.entry : [];
    return entries
      .map((e: any) => e?.resource)
      .filter((r: any) => !!r && r.resourceType);
  }

  private findNextLinkUrl(bundle: any): string | null {
    const links = Array.isArray(bundle?.link) ? bundle.link : [];
    const next = links.find((l: any) => String(l?.relation || '').toLowerCase() === 'next');
    const url = next?.url ? String(next.url).trim() : '';
    return url || null;
  }

  public normalizeNextLinkUrl(url: string | null | undefined, startUrl?: string): string | null {
    const trimmed = (url || '').trim();
    if (!trimmed) return null;

    const defaultBackend = (this.backendApiEndpoint || this.backendUrl || '').trim();
    const patientBackend = (this.patientBackendEndpoint || '').trim();

    // Determine target backend base URL:
    // If startUrl or next url is directed at patientBackend, normalize against patientBackend!
    let targetEndpoint = defaultBackend;
    if (patientBackend) {
      const isPatientUrl = (startUrl && (startUrl.includes(patientBackend) || /patientBackend/i.test(startUrl))) ||
                           trimmed.includes(patientBackend) ||
                           /patientBackend/i.test(trimmed);
      if (isPatientUrl) {
        targetEndpoint = patientBackend;
      }
    }

    return normalizeFhirNextLinkUrl(trimmed, targetEndpoint);
  }

  public normalizeFhirNextLinkUrl(url: string | null | undefined, targetEndpoint: string): string | null {
    return normalizeFhirNextLinkUrl(url, targetEndpoint);
  }

  /**
   * Fetches pages by following `Bundle.link[relation=next]` up to `maxPages`.
   * Returns an array of bundles (page 1..N). Errors are propagated.
   */
  private fetchBundlesFollowingNextLinks$(startUrl: string, maxPages: number, context?: HttpContext): Observable<any[]> {
    const firstUrl = (startUrl || '').toString().trim();
    if (!firstUrl) return of([] as any[]);
    const cap = typeof maxPages === 'number' && maxPages > 0 ? Math.floor(maxPages) : 10;

    return this.http.get<any>(firstUrl, context ? { context } : {}).pipe(
      expand((bundle, idx) => {
        if (idx + 1 >= cap) return EMPTY;
        const nextUrl = this.normalizeNextLinkUrl(this.findNextLinkUrl(bundle), firstUrl);
        if (!nextUrl) return EMPTY;
        return this.http.get<any>(nextUrl, context ? { context } : {});
      }),
      take(cap),
      reduce((acc: any[], b: any) => {
        acc.push(b);
        return acc;
      }, [] as any[])
    );
  }

  /**
   * Like `fetchBundlesFollowingNextLinks$`, but only keeps paging while the locally-filtered
   * result length is below `expectedMinLength`.
   */
  private fetchBundlesFollowingNextLinksUntil$(
    startUrl: string,
    maxPages: number,
    localFilters: Array<{ key: string; value: any }>,
    expectedMinLength: number,
    context?: HttpContext
  ): Observable<any[]> {
    const firstUrl = (startUrl || '').toString().trim();
    if (!firstUrl) return of([] as any[]);
    const cap = typeof maxPages === 'number' && maxPages > 0 ? Math.floor(maxPages) : 10;
    const expected = typeof expectedMinLength === 'number' && expectedMinLength > 0 ? Math.floor(expectedMinLength) : 0;

    type PageState = { bundle: any; page: number; accFilteredLen: number };
    const bundleFilteredLen = (bundle: any) => {
      const resources = this.extractResourcesFromBundle(bundle);
      const filtered = this.applyLocalFilters(resources, localFilters);
      return Array.isArray(filtered) ? filtered.length : 0;
    };

    return this.http.get<any>(firstUrl, context ? { context } : {}).pipe(
      map((bundle) => ({ bundle, page: 1, accFilteredLen: bundleFilteredLen(bundle) } as PageState)),
      expand((state) => {
        if (expected > 0 && state.accFilteredLen >= expected) return EMPTY;
        if (state.page >= cap) return EMPTY;
        const nextUrl = this.normalizeNextLinkUrl(this.findNextLinkUrl(state.bundle), firstUrl);
        if (!nextUrl) return EMPTY;
        return this.http.get<any>(nextUrl, context ? { context } : {}).pipe(
          map((bundle) => ({
            bundle,
            page: state.page + 1,
            accFilteredLen: state.accFilteredLen + bundleFilteredLen(bundle)
          } as PageState))
        );
      }),
      take(cap),
      map((s) => s.bundle),
      reduce((acc: any[], b: any) => {
        acc.push(b);
        return acc;
      }, [] as any[])
    );
  }

  private unwrapStateWrappers(wrappers: any[]): any[] {
    const list = Array.isArray(wrappers) ? wrappers : [];
    return list
      .map((w: any) => w?.actualResource)
      .filter((r: any) => !!r && r.resourceType && r.resourceType !== 'OperationOutcome');
  }

  private getStateSubjectForResourceType(
    resourceType: string,
    scope: 'patient' | 'orgWide'
  ): BehaviorSubject<any[]> | null {
    const key = this.resourceTypeToStateKey(resourceType);
    if (!key) return null;
    const base: any = scope === 'patient' ? (this.stateService as any).PatientResources : (this.stateService as any).orgWideResources;
    const subject = base?.[key];
    // Expecting BehaviorSubject<Array<{actualResource: Resource}>>
    return subject && typeof subject.asObservable === 'function' ? (subject as BehaviorSubject<any[]>) : null;
  }

  private resourceTypeToStateKey(resourceType: string): string | null {
    // Mirrors keys used in StateService.PatientResources and StateService.orgWideResources
    switch ((resourceType || '').trim()) {
      case 'Observation': return 'observations';
      case 'Condition': return 'condition';
      case 'MedicationRequest': return 'medicationRequests';
      case 'Specimen': return 'specimens';
      case 'Medication': return 'medications';
      case 'MedicationDispense': return 'medicationDispenses';
      case 'MedicationAdministration': return 'medicationAdministrations';
      case 'Encounter': return 'encounters';
      case 'ServiceRequest': return 'serviceRequests';
      case 'Task': return 'tasks';
      case 'ChargeItemDefinition': return 'chargeItemDefinitions';
      case 'ChargeItem': return 'chargeItems';
      case 'Invoice': return 'invoices';
      case 'DiagnosticReport': return 'diagnosticReports';
      case 'Procedure': return 'procedures';
      case 'ImagingStudy': return 'imagingStudies';
      case 'Location': return 'locations';
      case 'CodeSystem': return 'codeSystems';
      case 'ValueSet': return 'valueSets';
      case 'ActivityDefinition': return 'activityDefinitions';
      case 'SupplyDelivery': return 'supplyDeliveries';
      case 'Patient': return 'patient';
      case 'Slot': return 'slots';
      case 'AllergyIntolerance': return 'allergyIntolerances';
      case 'Appointment': return 'appointments';
      case 'Immunization': return 'immunizations';
      case 'Schedule': return 'schedules';
      default: return null;
    }
  }

  private buildFhirSearchUrl(
    resourceType: string,
    queryParams: Record<string, any>,
    count?: number,
    format?: 'json'
  ): string {
    const base = `${this.backendApiEndpoint}/${resourceType}`;
    const params = new URLSearchParams();

    const q = queryParams || {};
    for (const k of Object.keys(q)) {
      const v = (q as any)[k];
      if (v === undefined || v === null || v === '') continue;
      if (Array.isArray(v)) {
        v.forEach((item) => {
          if (item === undefined || item === null || item === '') return;
          params.append(k, String(item));
        });
      } else {
        params.set(k, String(v));
      }
    }

    params.set('_count', String(typeof count === 'number' && count > 0 ? count : 200));
    if (format) params.set('_format', format);

    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  /**
   * Variant of `buildFhirSearchUrl` that supports OR encoding.
   *
   * OR rules:
   * - If the value is `{ or: [...] }`, it is always encoded as comma-joined (FHIR OR within a parameter).
   * - If the value is an array and the key is in `orKeys`, it is encoded as comma-joined.
   * - Otherwise arrays are appended as repeated params (existing behavior).
   */
  private buildFhirSearchUrlWithOr(
    resourceType: string,
    queryParams: Record<string, any>,
    count?: number,
    format?: 'json',
    orKeys?: string[]
  ): string {
    const base = `${this.backendApiEndpoint}/${resourceType}`;
    const params = new URLSearchParams();
    const orKeySet = new Set((orKeys || []).map((k) => String(k || '').trim()).filter(Boolean));

    const isOrValue = (v: any): v is FhirSearchOrValue => {
      return !!v && typeof v === 'object' && !Array.isArray(v) && Array.isArray((v as any).or);
    };

    const q = queryParams || {};
    for (const k of Object.keys(q)) {
      const v = (q as any)[k];
      if (v === undefined || v === null || v === '') continue;

      if (isOrValue(v)) {
        const vals = (v.or || []).filter((item) => item !== undefined && item !== null && item !== '').map((item) => String(item));
        if (vals.length) params.set(k, vals.join(','));
        continue;
      }

      if (Array.isArray(v)) {
        const vals = v.filter((item) => item !== undefined && item !== null && item !== '').map((item) => String(item));
        if (!vals.length) continue;
        if (orKeySet.has(k)) {
          params.set(k, vals.join(','));
        } else {
          vals.forEach((item) => params.append(k, item));
        }
        continue;
      }

      params.set(k, String(v));
    }

    params.set('_count', String(typeof count === 'number' && count > 0 ? count : 200));
    if (format) params.set('_format', format);

    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  private applyLocalFilter<T extends any>(resources: T[], filterKey?: string, filterValue?: any): T[] {
    if (!filterKey) return resources;
    const key = this.normalizeFilterKey(filterKey);
    console.log('Applying local filter', key, filterValue);
    return (resources || []).filter((r: any) => this.resourceMatchesFilter(r, key, filterValue));
  }

  private applyLocalFilters<T extends any>(resources: T[], filters: Array<{ key: string; value: any }>): T[] {
    const list = Array.isArray(resources) ? resources : [];
    const fs = Array.isArray(filters) ? filters.filter((f) => !!f?.key) : [];
    if (!fs.length) return list;
    return list.filter((r: any) => fs.every((f) => this.resourceMatchesFilter(r, this.normalizeFilterKey(f.key), f.value)));
  }

  private normalizeFilterKey(key: string): string {
    const trimmed = (key || '').toString().trim();
    if (!trimmed) return trimmed;
    // Convert FHIR search-param style (based-on) to element name (basedOn)
    if (trimmed.includes('-') && !trimmed.includes('.')) {
      return trimmed.replace(/-([a-z])/g, (_, c) => String(c).toUpperCase());
    }
    return trimmed;
  }

  private resourceMatchesFilter(resource: any, key: string, expected: any): boolean {
    if (!resource || !key) return false;
    const values = this.getValuesAtPath(resource, key);
    return values.some((v) => {
      console.log('Matching value', v, 'against expected', expected);
      return this.valueMatches(v, expected)
    }
    );
  }

  private getValuesAtPath(obj: any, path: string): any[] {
    if (!obj || !path) return [];
    const parts = path.split('.').filter(Boolean);
    let current: any[] = [obj];

    for (const p of parts) {
      const next: any[] = [];
      for (const c of current) {
        if (c === undefined || c === null) continue;
        const raw = (c as any)[p];
        if (Array.isArray(raw)) next.push(...raw);
        else if (raw !== undefined) next.push(raw);
      }
      current = next;
      if (!current.length) break;
    }

    return current;
  }

  private valueMatches(actual: any, expected: any): boolean {
    if (expected === undefined || expected === null) return true;
    if (actual === undefined || actual === null) return false;

    // OR matching: expected can be an array (e.g. ['final','registered'])
    if (Array.isArray(expected)) {
      return expected.some((e) => this.valueMatches(actual, e));
    }

    // OR matching: expected can be { or: [...] }
    if (expected && typeof expected === 'object' && Array.isArray((expected as any).or)) {
      return (expected as any).or.some((e: any) => this.valueMatches(actual, e));
    }

    // Match strings
    if (typeof expected === 'string') {
      const exp = expected.trim();
      if (!exp) return true;
      if (typeof actual === 'string') {
        if (actual === exp) return true;
        return this.referenceStringMatches(actual, exp);
      }

      // Reference
      if (typeof actual === 'object' && actual.reference) {
        const a = String(actual.reference);
        return a === exp || this.referenceStringMatches(a, exp);
      }

      // CodeableConcept
      if (typeof actual === 'object' && (actual.coding || actual.text)) {
        const text = this.chooseFirstStringFromCodeableConcept(actual);
        if (text && text.toLowerCase() === exp.toLowerCase()) return true;
        const coding = Array.isArray(actual.coding) ? actual.coding : [];
        return coding.some((c: any) => String(c.code || '') === exp || String(c.display || '').toLowerCase() === exp.toLowerCase());
      }

      // Coding-like
      if (typeof actual === 'object' && (actual.code || actual.display)) {
        return String(actual.code || '') === exp || String(actual.display || '').toLowerCase() === exp.toLowerCase();
      }

      return String(actual) === exp;
    }

    // Match Coding-like objects {system, code}
    if (typeof expected === 'object' && (expected.code || expected.system)) {
      const expCode = String(expected.code || '').trim();
      const expSystem = String(expected.system || '').trim();
      if (!expCode && !expSystem) return true;

      // actual is Coding
      if (typeof actual === 'object' && (actual.code || actual.system)) {
        const aCode = String(actual.code || '').trim();
        const aSystem = String(actual.system || '').trim();
        return (!!expCode ? aCode === expCode : true) && (!!expSystem ? aSystem === expSystem : true);
      }

      // actual is CodeableConcept
      if (typeof actual === 'object' && (actual.coding || actual.text)) {
        const coding = Array.isArray(actual.coding) ? actual.coding : [];
        return coding.some((c: any) => {
          const aCode = String(c?.code || '').trim();
          const aSystem = String(c?.system || '').trim();
          return (!!expCode ? aCode === expCode : true) && (!!expSystem ? aSystem === expSystem : true);
        });
      }
    }

    // Match Reference-like expected
    if (typeof expected === 'object' && expected.reference) {
      const expRef = String(expected.reference || '').trim();
      if (typeof actual === 'string') return actual === expRef || this.referenceStringMatches(actual, expRef);
      if (typeof actual === 'object' && actual.reference) {
        const a = String(actual.reference);
        return a === expRef || this.referenceStringMatches(a, expRef);
      }
    }

    // Fallback strict equality
    return actual === expected;
  }

  /**
   * OR-capable variant of `threeTierQueryAndSyncToState$`.
   *
   * This is a thin wrapper over `threeTierQueryAndSyncToState$`, so it also supports:
   * - PatientId enforcement when `scope:'patient'` or `useCurrentPatientId:true`
   * - Tier-3 conditional paging via `Bundle.link[relation=next]` (see `tier3FollowNextLinks`, `expectedMinLength`)
   *
   * Usage examples:
   * - URL OR: urlSearchObject: { status: { or: ['final','registered'] } }
   * - URL OR (array + keys): urlSearchObject: { status: ['final','registered'] }, urlOrKeys:['status']
   * - Client OR: querySearchObject: { key:'status', value: ['final','registered'] }
   */
  threeTierQueryAndSyncToStateOr$<T extends FhirResource = FhirResource>(args: ThreeTierFhirQuery): Observable<T[]> {
    return this.threeTierQueryAndSyncToState$(args);
  }

  /**
   * One-shot OR-capable variant of `threeTierQueryAndSyncToStateOnce$`.
   * Inherits the same patientId enforcement and tier-3 paging options as the base method.
   */
  threeTierQueryAndSyncToStateOnceOr$<T extends FhirResource = FhirResource>(args: ThreeTierFhirQuery): Observable<T[]> {
    return this.threeTierQueryAndSyncToStateOnce$(args);
  }

  /**
   * Cloned variant of `threeTierQueryAndSyncToStateOnce$` with stricter, "exhaustive server" semantics:
   * - Never consults state cache.
   * - Caller MUST provide `patientIds` (string[] | null). `null` means org-wide.
   * - When `followNextLinksExhaustively=true` the method will follow next-links until none remain
   *   (practically bounded by a very large page cap).
   * - If `failOnOperationOutcome=true` any OperationOutcome in any page will cause an immediate error.
   * - Uses exactly one server-side search object (`urlSearchObject`) and one local-filter list (`querySearchObject`).
   */
  threeTierQueryAndSyncToStateOnceExhaustive$<T extends FhirResource = FhirResource>(args: {
    resourceType: string;
    // server-side params (one object)
    urlSearchObject?: Record<string, any>;
    // local filter param(s) applied after tier3 fetch
    querySearchObject?: Array<{ key: string; value: any }> | { key: string; value: any };
    // REQUIRED: either an array of patient ids (['a','b']) or null for org-wide
    patientIds: string[] | null;
    // name of the patient-scoped search param (default 'subject')
    patientScopeSearchParam?: string;
    // follow next links until exhausted when true
    followNextLinksExhaustively?: boolean;
    // when true any OperationOutcome observed will cause an error
    failOnOperationOutcome?: boolean;
    // how many results to request per page
    count?: number;
    // optional format
    format?: 'json';
    // expected minimum length to short-circuit tier3
    expectedMinLength?: number;
    // http context
    context?: HttpContext;
  }): Observable<T[]> {
    return defer(() => {
      const resourceType = (args?.resourceType || '').toString().trim();
      if (!resourceType) return of([] as T[]);

      // Normalize local filters to the simple array form (one local filter input expected conceptually)
      const localFilters: Array<{ key: string; value: any }> = [];
      if (Array.isArray(args.querySearchObject)) localFilters.push(...args.querySearchObject.filter((f) => !!f?.key));
      else if (args.querySearchObject && typeof args.querySearchObject === 'object' && (args.querySearchObject as any).key) localFilters.push(args.querySearchObject as any);

      // Enforce presence of patientIds (caller must provide explicit scope decision)
      if (typeof args.patientIds === 'undefined') {
        return throwError(() => new Error('threeTierQueryAndSyncToStateOnceExhaustive$ requires explicit `patientIds` (string[] | null) in args'));
      }

      const patientIds = args.patientIds;
      if (Array.isArray(patientIds) && patientIds.length === 0) {
        return throwError(() => new Error('threeTierQueryAndSyncToStateOnceExhaustive$ requires non-empty patientIds array or null for org-wide'));
      }

      const patientParamName = (args.patientScopeSearchParam || 'subject').toString().trim() || 'subject';
      const count = typeof args.count === 'number' && args.count > 0 ? Math.floor(args.count) : this.fhirDefaultCount;
      const expectedMinLength = typeof args.expectedMinLength === 'number' ? Math.max(0, Math.floor(args.expectedMinLength)) : 0;
      const failOnOperationOutcome = args.failOnOperationOutcome === true;
      const followExhaustive = args.followNextLinksExhaustively === true;

      // Build tier-1 server search object by merging provided server params and enforced patient param
      const urlSearchObj: Record<string, any> = Object.assign({}, args.urlSearchObject || {});
      if (patientIds !== null) {
        // For patient-scoped search, encode as OR within the param using { or: [...] }
        const vals = patientIds.map((id) => (patientParamName === 'patient' ? id : `Patient/${id}`));
        urlSearchObj[patientParamName] = { or: vals };
      }

      const queryUrl = this.buildFhirSearchUrlWithOr(resourceType, urlSearchObj, count, args.format, [patientParamName]);

      // Practical cap when following exhaustively (very large)
      const maxPages = followExhaustive ? 1000000 : (typeof this.fhirMaxAllAtOnceCount === 'number' && this.fhirMaxAllAtOnceCount > 0 ? this.fhirMaxAllAtOnceCount : 10);

      const ctx = args.context;

      // Tier-1: fetch pages (follow next-links depending on flag)
      const tier1Bundles$ = this.fetchBundlesFollowingNextLinks$(queryUrl, maxPages, ctx);

      const tier1Resources$ = tier1Bundles$.pipe(
        map((bundles: any[]) => {
          // If caller requested fail-on-operation-outcome, fail immediately when any page contains OO
          if (failOnOperationOutcome) {
            for (const b of (bundles || [])) {
              if (this.isOperationOutcomeBundle(b)) throw new Error('OperationOutcome received during tier1 fetch');
            }
          }

          // Collect resources (only those with matching resourceType)
          const collected: T[] = [];
          for (const b of (bundles || [])) {
            const entries = Array.isArray(b?.entry) ? b.entry : [];
            for (const e of entries) {
              const r = e?.resource;
              if (r && r.resourceType === resourceType) collected.push(r as T);
            }
          }
          return collected;
        }),
        map((resources: T[]) => this.applyLocalFilters(resources, localFilters) as T[])
      );

      // Tier-3: reduced server params (only patient constraint if provided) then local filtering
      const tier3UrlSearch: Record<string, any> = {};
      if (patientIds !== null) {
        const vals = patientIds.map((id) => (patientParamName === 'patient' ? id : `Patient/${id}`));
        tier3UrlSearch[patientParamName] = { or: vals };
      }
      const tier3Url = this.buildFhirSearchUrlWithOr(resourceType, tier3UrlSearch, count, args.format, [patientParamName]);
      const tier3Bundles$ = this.fetchBundlesFollowingNextLinks$(tier3Url, maxPages, ctx);

      const tier3Resources$ = tier3Bundles$.pipe(
        map((bundles: any[]) => {
          if (failOnOperationOutcome) {
            for (const b of (bundles || [])) {
              if (this.isOperationOutcomeBundle(b)) throw new Error('OperationOutcome received during tier3 fetch');
            }
          }
          const collected: T[] = [];
          for (const b of (bundles || [])) {
            const entries = Array.isArray(b?.entry) ? b.entry : [];
            for (const e of entries) {
              const r = e?.resource;
              if (r && r.resourceType === resourceType) collected.push(r as T);
            }
          }
          return this.applyLocalFilters(collected, localFilters) as T[];
        })
      );

      // Decide: if tier1 produced expectedMinLength or more -> use it; otherwise use tier3
      return tier1Resources$.pipe(
        switchMap((t1: T[]) => {
          if (Array.isArray(t1) && t1.length >= expectedMinLength) return of(t1);
          return tier3Resources$;
        })
      );
    });
  }

  private getOrgWideSubject(resourceType: string): BehaviorSubject<Array<{ referenceId: string | null, savedStatus: 'saved' | 'unsaved', actualResource: any }>> | null {
    if (!resourceType) return null;
    switch (resourceType) {
      case 'Patient': return this.stateService.orgWideResources.patient;
      case 'Observation': return this.stateService.orgWideResources.observations;
      case 'Condition': return this.stateService.orgWideResources.condition;
      case 'MedicationRequest': return this.stateService.orgWideResources.medicationRequests;
      case 'Specimen': return this.stateService.orgWideResources.specimens;
      case 'Medication': return this.stateService.orgWideResources.medications;
      case 'MedicationDispense': return this.stateService.orgWideResources.medicationDispenses;
      case 'MedicationAdministration': return this.stateService.orgWideResources.medicationAdministrations;
      case 'Encounter': return this.stateService.orgWideResources.encounters;
      case 'Procedure': return this.stateService.orgWideResources.procedures;
      case 'ImagingStudy': return this.stateService.orgWideResources.imagingStudies;
      case 'ServiceRequest': return this.stateService.orgWideResources.serviceRequests;
      case 'HealthcareService': return this.stateService.orgWideResources.healthcareServices;
      case 'Task': return this.stateService.orgWideResources.tasks;
      case 'Location': return this.stateService.orgWideResources.locations;
      case 'CodeSystem': return this.stateService.orgWideResources.codeSystems;
      case 'ValueSet': return this.stateService.orgWideResources.valueSets;
      case 'ChargeItemDefinition': return this.stateService.orgWideResources.chargeItemDefinitions;
      case 'ChargeItem': return this.stateService.orgWideResources.chargeItems;
      case 'Invoice': return this.stateService.orgWideResources.invoices;
      case 'DiagnosticReport': return this.stateService.orgWideResources.diagnosticReports;
      case 'ActivityDefinition': return this.stateService.orgWideResources.activityDefinitions;
      case 'SupplyDelivery': return this.stateService.orgWideResources.supplyDeliveries;
      case 'Schedule': return this.stateService.orgWideResources.schedules;
      case 'Appointment': return this.stateService.orgWideResources.appointments;
      case 'Slot': return this.stateService.orgWideResources.slots;
      case 'Immunization': return this.stateService.orgWideResources.immunizations;
      default: return null;
    }
  }

  public fetchAll<T extends Resource>(args: {
    resourceType: string;
    queries: string | Record<string, any>;
    context?: HttpContext;
    checkOrgWideState?: boolean;
    updateOrgWideState?: boolean;
    localFilters?: Array<{ key: string; value: any }>;
    maxPages?: number;
    stopWhen?: (resources: T[]) => boolean;
  }): Observable<T[]> {
    const { resourceType, queries, context, checkOrgWideState, updateOrgWideState, localFilters, maxPages, stopWhen } = args;
    const limit = maxPages ?? 100;
    const ctx = context || new HttpContext();
    
    // Always disable auto-count interceptor so we can manually manage _count and _summary
    ctx.set(DisableCountParam, true);

    const queryStr = typeof queries === 'string' 
      ? (queries.startsWith('?') ? queries.substring(1) : queries)
      : Object.entries(queries).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
    
    const baseUrl = `${this.backendApiEndpoint}/${resourceType}`;
    const baseQuery = queryStr ? `${baseUrl}?${queryStr}` : baseUrl;
    const separator = baseQuery.includes('?') ? '&' : '?';

    // 1. Check state if requested
    const stateCheck$ = (() => {
      if (!checkOrgWideState) return of(null);
      const subject = this.getOrgWideSubject(resourceType);
      if (!subject) return of(null);
      
      const stateItems = subject.getValue().map(x => x.actualResource) as T[];
      const filtered = this.applyLocalFilters(stateItems, localFilters ?? []);
      
      // Early exit if stopWhen is provided and satisfies cache
      if (typeof stopWhen === 'function') {
        if (stopWhen(filtered)) return of(filtered);
        return of(null);
      }

      // Fallback to strict validation via summary=count
      const summaryUrl = `${baseQuery}${separator}_summary=count`;
      return this.http.get<any>(summaryUrl, { context: ctx }).pipe(
        switchMap(bundle => {
          const total = bundle?.total ?? 0;
          if (total > 0 && filtered.length === total) {
            return of(filtered);
          }
          return of(null);
        }),
        catchError(() => of(null))
      );
    })();

    // 2. Fetch from server if state check fails or is skipped
    return stateCheck$.pipe(
      switchMap((cachedState: T[] | null) => {
        if (cachedState !== null) {
          return of(cachedState);
        }

        const fetchUrl = `${baseQuery}${separator}_count=${this.fhirMaxAllAtOnceCount}`;
        
        type PageState = { url: string | null, resources: T[], pageCount: number, done: boolean };

        return this.http.get<any>(fetchUrl, { context: ctx }).pipe(
          map(bundle => {
            const rawResources = this.extractResourcesFromBundle(bundle);
            const res = rawResources.filter(r => r.resourceType === resourceType) as T[];
            const matching = this.applyLocalFilters(res, localFilters ?? []);
            const done = typeof stopWhen === 'function' && stopWhen(matching);
            
            return {
              url: this.normalizeNextLinkUrl(this.findNextLinkUrl(bundle), fetchUrl),
              resources: matching,
              pageCount: 1,
              done: done
            } as PageState;
          }),
          expand(state => {
            if (state.done || !state.url || state.pageCount >= limit) return EMPTY;
            return this.http.get<any>(state.url, { context: ctx }).pipe(
              map(bundle => {
                const rawResources = this.extractResourcesFromBundle(bundle);
                const res = rawResources.filter(r => r.resourceType === resourceType) as T[];
                const matching = this.applyLocalFilters(res, localFilters ?? []);
                const combined = [...state.resources, ...matching];
                const done = typeof stopWhen === 'function' && stopWhen(combined);
                
                return {
                  url: this.normalizeNextLinkUrl(this.findNextLinkUrl(bundle), fetchUrl),
                  resources: combined,
                  pageCount: state.pageCount + 1,
                  done: done
                } as PageState;
              })
            );
          }),
          reduce((acc, curr) => curr, { url: null, resources: [], pageCount: 0, done: false } as PageState),
          map(finalState => {
            const allResources = finalState.resources;
            if (updateOrgWideState) {
              allResources.forEach(r => this.stateService.persistOrgWideResource(r, 'saved'));
            }
            return allResources;
          })
        );
      })
    );
  }

  private stripHistoryFromRef(reference: string): string {
    const ref = (reference || '').toString().trim();
    const idx = ref.indexOf('/_history/');
    return idx >= 0 ? ref.slice(0, idx) : ref;
  }

  private looksLikeRelativeReference(reference: string): boolean {
    const r = (reference || '').toString().trim();
    // e.g. Patient/123, ServiceRequest/abc-1
    return /^[A-Za-z][A-Za-z0-9]*\/[A-Za-z0-9\-\.]{1,128}$/.test(r);
  }

  private referenceStringMatches(actual: string, expected: string): boolean {
    const a = this.stripHistoryFromRef(actual);
    const e = this.stripHistoryFromRef(expected);
    if (!a || !e) return false;
    if (a === e) return true;
    // Allow absolute-vs-relative matching for reference-like strings.
    if (this.looksLikeRelativeReference(e)) {
      return a.endsWith(e);
    }
    return false;
  }

  private mapReferenceValue(val: any): any {
    if (typeof val === 'string') {
      return { reference: val };
    }
    if (Array.isArray(val)) {
      return val
        .filter(v => !!v)
        .map(v => typeof v === 'string' ? { reference: v } : v.reference ? v : null)
        .filter(v => !!v);
    }
    if (val && typeof val === 'object' && val.reference) {
      return val;
    }
    return val;
  }

  private mapAttachments(val: any): any {
    if (!val) return [];
    const arr = Array.isArray(val) ? val : [val];
    return arr
      .filter(v => v && (v.data || v.url))
      .map(v => ({
        contentType: v.type || v.contentType || 'application/octet-stream',
        data: v.data, // base64 expected
        url: v.url,
        title: v.name || v.title
      }))
      .filter(a => a.data || a.url);
  }

  /**
   * Generate a requisition identifier for FHIR resources.
   * Requisitions are used to track orders/requests in healthcare settings (lab tests, imaging, procedures).
   *
   * @param opts Configuration options
   * @param opts.prefix Prefix for the requisition number (default: 'REQ')
   * @param opts.resourceType Type of resource: 'lab', 'service', 'imaging', 'specimen' (default: 'lab')
   * @param opts.facilityCode Short facility/department code (default: 'FAC')
   * @param opts.system System URL for the identifier (default: hospital system URL)
   * @param opts.includeTimestamp Whether to include timestamp in the requisition (default: true)
   * @returns FHIR Identifier object with requisition number
   *
   * @example
   * // Generates: { system: '...', value: 'REQ-LAB-FAC-20251118-001234' }
   * generateRequisition({ resourceType: 'lab', facilityCode: 'LAB' })
   *
   * @example
   * // Generates: { system: '...', value: 'SRV-IMG-RAD-20251118-001234' }
   * generateRequisition({ prefix: 'SRV', resourceType: 'imaging', facilityCode: 'RAD' })
   */
  generateRequisition(opts?: {
    prefix?: string;
    resourceType?: 'lab' | 'service' | 'medReq' | 'imaging' | 'specimen' | 'procedure' | 'consultation';
    facilityCode?: string;
    system?: string;
    includeTimestamp?: boolean;
  }): { system: string; value: string; type?: any } {
    const prefix = opts?.prefix || 'REQ';
    const facilityCode = opts?.facilityCode || 'FAC';
    const includeTimestamp = opts?.includeTimestamp !== false;
    const system = opts?.system || 'http://hospital.example.org/requisition';

    // Resource type codes for clarity
    const typeCodeMap = {
      lab: 'LAB',
      medReq: 'MEDREQ',
      service: 'SRV',
      imaging: 'IMG',
      specimen: 'SPM',
      procedure: 'PRO',
      consultation: 'CON'
    };
    const typeCode = opts?.resourceType ? typeCodeMap[opts.resourceType] : 'GEN';

    // Generate timestamp component (YYYYMMDD format)
    const now = new Date();
    const dateStr = includeTimestamp
      ? now.toISOString().slice(0, 10).replace(/-/g, '') // YYYYMMDD
      : '';

    // Generate unique sequence number (6 digits)
    // In production, this should come from a database sequence or atomic counter
    const sequence = this.generateSequenceNumber(6);

    // Build requisition number: PREFIX-TYPE-FACILITY-DATE-SEQUENCE
    const parts = [prefix, typeCode, facilityCode];
    if (dateStr) parts.push(dateStr);
    parts.push(sequence);

    const requisitionValue = parts.join('-');

    return {
      system,
      value: requisitionValue,
      type: {
        coding: [{
          system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
          code: 'PLAC',
          display: 'Placer Identifier'
        }],
        text: 'Requisition Number'
      }
    };
  }

  /**
   * Generate a unique sequence number for requisitions.
   * Uses timestamp + sub-second counter for uniqueness across sessions.
   * In production, replace this with a database sequence or distributed ID generator.
   *
   * @param length Number of digits in the sequence (default: 6)
   * @returns Zero-padded sequence number as string
   */
  private generateSequenceNumber(length: number = 6): string {
    const now = Date.now();

    // If same millisecond, increment sub-counter; otherwise reset
    if (now === this.lastSequenceTimestamp) {
      this.sequenceSubCounter++;
    } else {
      this.lastSequenceTimestamp = now;
      this.sequenceSubCounter = 0;
    }

    // Combine: last 4 digits of timestamp + 2-digit sub-counter
    // This ensures uniqueness across app restarts and rapid calls
    const timePart = now % 10000; // Last 4 digits of timestamp
    const combined = (timePart * 100 + this.sequenceSubCounter) % (10 ** length);

    return String(combined).padStart(length, '0');
  }

  /**
   * Generate a locally unique FHIR Identifier.
   * - Prefers Web Crypto `crypto.randomUUID()` when available.
   * - Falls back to timestamp + sequence + random suffix.
   *
   * Note: This is "unique enough" for client-side identifiers; true global uniqueness
   * still depends on your server-side constraints.
   */
  generateLocalIdentifier(opts?: {
    system?: string;
    prefix?: string;
  }): Identifier {
    const system = (opts?.system ?? 'urn:elikita:local-identifier').toString();
    const prefix = (opts?.prefix ?? 'id').toString();

    const anyCrypto: any = (typeof crypto !== 'undefined' ? (crypto as any) : null);
    const uuid = anyCrypto?.randomUUID?.();
    if (uuid) {
      return { system, value: `${prefix}-${uuid}` } as Identifier;
    }

    const ts = new Date().toISOString().replace(/[-:.TZ]/g, '');
    const seq = this.generateSequenceNumber(6);
    const rand = Math.random().toString(16).slice(2);
    return { system, value: `${prefix}-${ts}-${seq}-${rand}` } as Identifier;
  }

  /**
   * Generate requisition identifier and add it to a FHIR resource.
   *
   * @param resource The FHIR resource to add requisition to
   * @param opts Requisition generation options
   * @returns The resource with requisition identifier added
   *
   * @example
   * const serviceRequest = { resourceType: 'ServiceRequest', ... };
   * addRequisitionToResource(serviceRequest, { resourceType: 'imaging', facilityCode: 'RAD' });
   */
  addRequisitionToResource(
    resource: any,
    opts?: Parameters<typeof this.generateRequisition>[0]
  ): any {
    if (!resource) return resource;

    const requisition = this.generateRequisition(opts);

    // Add to identifier array if it exists, otherwise create it
    if (!resource.identifier) {
      resource.identifier = [];
    }
    if (!Array.isArray(resource.identifier)) {
      resource.identifier = [resource.identifier];
    }

    // Check if requisition already exists (avoid duplicates)
    const hasRequisition = resource.identifier.some(
      (id: any) => id.type?.coding?.some((c: any) => c.code === 'PLAC')
    );

    if (!hasRequisition) {
      resource.identifier.push(requisition);
    }

    return resource;
  }

  /**
   * Extract requisition number from a FHIR resource's identifiers.
   *
   * @param resource FHIR resource with identifiers
   * @returns Requisition value or null if not found
   */
  getRequisitionFromResource(resource: any): string | null {
    if (!resource?.identifier) return null;

    const identifiers = Array.isArray(resource.identifier)
      ? resource.identifier
      : [resource.identifier];

    const requisition = identifiers.find(
      (id: any) => id.type?.coding?.some((c: any) => c.code === 'PLAC')
    );

    return requisition?.value || null;
  }

  retrieveResourceFromEntry(entry: BundleEntry<FhirResource> | FhirResource): FhirResource {
    if ('resource' in entry) {
      return entry.resource as FhirResource;
    }

    return entry as FhirResource;
  }
  backendApiEndpoint = inject(backendEndPointToken);
  patientBackendEndpoint = inject(patientBackendEndPointToken, { optional: true });
  private readonly terminologyCanonicalUrlBase = inject(terminologyCanonicalUrlBaseToken);
  private readonly serviceRequestCategories = inject(serviceRequestCategoriesToken);
  private readonly substanceCodeCategories = inject(substanceCodeCategoriesToken);

  /**
   * Returns the hard-coded SubstanceCode categories as FHIR Coding[] options for UI.
   * Use this to populate dropdowns, select fields, etc.
   */
  getSubstanceCodeCategoryOptions(): Coding[] {
    const list = Array.isArray(this.substanceCodeCategories) ? this.substanceCodeCategories : [];
    return list
      .filter((c) => !!c?.code)
      .map((c) => ({ code: c.code, display: c.display, system: c.system } as Coding));
  }

  /**
   * Fetch SubstanceCode options for a given category.
   *
   * This fetches the category's ValueSet by canonical URL and maps compose.include[].concept[]
   * into a FHIR Coding[] list. This works even when the server cannot $expand.
   */
  getSubstanceCodesByCategory(categoryCode: string): Observable<Coding[]> {
    const canonicalUrl = this.buildSubstanceCodeValueSetCanonicalUrl(categoryCode);
    if (!canonicalUrl) return of([] as Coding[]);

    const cached = this.substanceCodeValueSetCache.get(canonicalUrl);
    if (cached) return cached;

    const searchUrl = `${this.backendApiEndpoint}/ValueSet?url=${encodeURIComponent(canonicalUrl)}&_count=200`;

    const request$ = this.http.get<any>(searchUrl).pipe(
      map((bundle: any) => {
        const valueSet = bundle?.entry?.[0]?.resource;
        const includes = Array.isArray(valueSet?.compose?.include) ? valueSet.compose.include : [];

        const out: Coding[] = [];
        for (const inc of includes) {
          const system = (inc?.system || '').toString();
          const concepts = Array.isArray(inc?.concept) ? inc.concept : [];
          for (const c of concepts) {
            const code = (c?.code ?? '').toString().trim();
            if (!code) continue;
            const display = (c?.display ?? code).toString();
            out.push({ code, display, system } as Coding);
          }
        }

        // De-dupe by code+system
        const seen = new Set<string>();
        return out.filter((c) => {
          const key = `${c.system || ''}::${c.code || ''}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }),
      catchError(() => of([] as Coding[])),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.substanceCodeValueSetCache.set(canonicalUrl, request$);
    return request$;
  }

  private buildSubstanceCodeValueSetCanonicalUrl(categoryCode: string): string {
    const code = (categoryCode || '').trim().toLowerCase();
    if (!code) return '';

    // Uses the same naming pattern as postman.substanceCode.transaction.json
    // e.g. ValueSet/Substance/SubstanceCodeChemical
    const suffix = this.toTitleCaseWord(code);
    return `${this.terminologyCanonicalUrlBase}/ValueSet/Substance/SubstanceCode${suffix}`;
  }

  /**
   * Loads the full ServiceRequest CodeSystem (includes active + inactive codes).
   * This is preferred when you need to display inactive codes and/or show status badges.
   */
  getAllServiceRequestCodesFromCodeSystem(): Observable<ServiceRequestCodeOptionWithStatus[]> {
    console.log('Fetching all ServiceRequest codes from CodeSystem');
    if (this.allServiceRequestCodesWithStatus$) return this.allServiceRequestCodesWithStatus$.pipe(tap((e) => console.log('Returning cached ServiceRequest codes with status', e)));

    const codeSystemUrl = `${this.terminologyCanonicalUrlBase}/CodeSystem/ServiceRequest/ServiceRequestCodes`;
    const searchUrl = `${this.backendApiEndpoint}/CodeSystem?url=${encodeURIComponent(codeSystemUrl)}&_count=2000`;

    // A ServiceRequest code is only "active/available" if it has an ActivityDefinition configured.
    // ActivityDefinitions are authored by service-details.component.ts and linked via ActivityDefinition.code.coding[].code.
    const activityDefinitionSearchUrl = `${this.backendApiEndpoint}/ActivityDefinition?_count=2000`;

    this.allServiceRequestCodesWithStatus$ = forkJoin({
      codeSystemBundle: this.http.get<any>(searchUrl).pipe(catchError(() => of(null))),
      activityDefinitionBundle: this.http.get<any>(activityDefinitionSearchUrl).pipe(catchError(() => of(null)))
    }).pipe(
      map(({ codeSystemBundle, activityDefinitionBundle }) => {
        const activityEntries = Array.isArray(activityDefinitionBundle?.entry) ? activityDefinitionBundle.entry : [];
        const activityDefs = activityEntries
          .map((e: any) => e?.resource)
          .filter((r: any) => r?.resourceType === 'ActivityDefinition' && (r as ActivityDefinition).topic?.some((t: any) => t.text === 'items usage computation'));
        // (debug) activityDefs length: activityDefs.length

        const activityCodeSet = new Set<string>();
        for (const ad of activityDefs) {
          const codings = Array.isArray(ad?.code?.coding) ? ad.code.coding : [];
          for (const coding of codings) {
            const code = (coding?.code ?? '').toString().trim().toLowerCase();
            if (code) activityCodeSet.add(code);
          }
        }

        const entries = Array.isArray(codeSystemBundle?.entry) ? codeSystemBundle.entry : [];
        const codeSystem = entries
          .map((e: any) => e?.resource)
          .find((r: any) => r?.resourceType === 'CodeSystem' && Array.isArray(r?.concept));

        const concepts = Array.isArray(codeSystem?.concept) ? codeSystem.concept : [];
        const system = (codeSystem?.url || codeSystemUrl) as string;

        const out: ServiceRequestCodeOptionWithStatus[] = [];
        for (const c of concepts) {
          const code = (c?.code ?? '').toString().trim();
          if (!code) continue;

          const display = (c?.display ?? code).toString();
          const props = Array.isArray(c?.property) ? c.property : [];
          const category = props.find((p: any) => p?.code === 'category')?.valueString as string | undefined;
          const activeProp = props.find((p: any) => p?.code === 'active');
          const activeFromCodeSystem = activeProp?.valueBoolean !== false;

          const hasActivityDefinition = activityCodeSet.has(code.toLowerCase());
          const active = activeFromCodeSystem && hasActivityDefinition;

          out.push({ code, display, system, category, active, hasActivityDefinition });
        }

        return out;
      }),
      catchError(() => of([] as ServiceRequestCodeOptionWithStatus[])),
      // shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.allServiceRequestCodesWithStatus$;
  }

  /**
   * Searches ServiceRequest codes using the full CodeSystem (includes inactive).
   * Filters by category (if supplied) and the search term client-side.
   */
  searchServiceRequestCodeFromCodeSystem(
    searchTerm: string,
    categoryCode?: string | null
  ): Observable<ServiceRequestCodeOptionWithStatus[]> {
    const normalizedTerm = (searchTerm || '').trim().toLowerCase();

    const categoryDisplay = (categoryCode
      ? (this.serviceRequestCategories || []).find((c) => c.code === categoryCode)?.display
      : undefined);
    const normalizedCategory = (categoryDisplay || (categoryCode ? this.toTitleCaseWord(categoryCode) : ''))
      .trim()
      .toLowerCase();

    return this.getAllServiceRequestCodesFromCodeSystem().pipe(
      map((options) => {
        let filtered = options;

        if (normalizedCategory) {
          filtered = filtered.filter((opt) => (opt.category || '').toLowerCase() === normalizedCategory);
        }

        if (!normalizedTerm) return filtered;
        return filtered.filter((opt) => {
          const display = (opt.display || '').toLowerCase();
          const code = (opt.code || '').toLowerCase();
          return display.includes(normalizedTerm) || code.includes(normalizedTerm);
        });
      })
    );
  }

  /**
   * Cached search for ServiceRequest codes.
   * - Fetches per-category ValueSet concepts (cached)
   * - Filters by `searchTerm` client-side
   */
  searchServiceRequestCode(searchTerm: string, categoryCode?: string | null): Observable<ServiceRequestCodeOption[]> {
    const normalizedTerm = (searchTerm || '').trim().toLowerCase();
    return this.getServiceRequestCodesByCategory(categoryCode).pipe(
      map((options) => {
        if (!normalizedTerm) return options;
        return options.filter((opt) => {
          const display = (opt.display || '').toLowerCase();
          const code = (opt.code || '').toLowerCase();
          return display.includes(normalizedTerm) || code.includes(normalizedTerm);
        });
      })
    );
  }

  /**
   * Returns all ServiceRequest codes across all categories (active only), cached.
   * De-dupes by `code` + `system`.
   */
  getAllServiceRequestCodesActive(): Observable<ServiceRequestCodeOption[]> {
    if (this.allServiceRequestCodesActive$) return this.allServiceRequestCodesActive$;

    const categories = (this.serviceRequestCategories || []).map((c) => c.code).filter(Boolean);
    const requests = categories.map((category) => this.getServiceRequestCodesByCategory(category));

    this.allServiceRequestCodesActive$ = forkJoin(requests).pipe(
      map((lists) => {
        const flat = lists.flat();
        const seen = new Set<string>();
        const unique: ServiceRequestCodeOption[] = [];

        for (const opt of flat) {
          const key = `${opt.code}::${opt.system}`;
          if (seen.has(key)) continue;
          seen.add(key);
          unique.push(opt);
        }

        return unique;
      }),
      catchError(() => of([])),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.allServiceRequestCodesActive$;
  }

  /**
   * Determines the category of a ServiceRequest code.
   * If the same code exists in multiple categories, returns the first match.
   */
  getServiceRequestCategoryForCode(code: string): Observable<{ categoryCode: string; categoryDisplay?: string; categorySystem?: string } | null> {
    const normalized = (code || '').trim();
    if (!normalized) return of(null);

    return this.getServiceRequestCodeToCategoryMap().pipe(
      map((m) => {
        const categoryCode = m.get(normalized) || null;
        console.log('Determined category for code', normalized, '=>', categoryCode);
        if (!categoryCode) return null;
        const meta = (this.serviceRequestCategories || []).find((c) => c.code === categoryCode);

        return {
          categoryCode,
          categoryDisplay: meta?.display,
          categorySystem: meta?.system
        };
      })
    );
  }

  private getServiceRequestCodeToCategoryMap(): Observable<Map<string, string>> {
    if (this.serviceRequestCodeToCategory$) return this.serviceRequestCodeToCategory$;

    const categories = (this.serviceRequestCategories || []).filter((c) => !!c?.code);
    const requests = categories.map((c) =>
      this.getServiceRequestCodesByCategory(c.code).pipe(
        map((options) => ({ categoryCode: c.code, options }))
      )
    );

    this.serviceRequestCodeToCategory$ = forkJoin(requests).pipe(
      map((results) => {
        const m = new Map<string, string>();
        for (const r of results) {
          for (const opt of r.options) {
            if (!opt?.code) continue;
            if (!m.has(opt.code)) {
              m.set(opt.code, r.categoryCode);
            }
          }
        }
        return m;
      }),
      catchError(() => of(new Map<string, string>())),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.serviceRequestCodeToCategory$;
  }

  private getServiceRequestCodesByCategory(categoryCode?: string | null): Observable<ServiceRequestCodeOption[]> {
    const canonicalUrl = this.buildServiceRequestCodesCanonicalUrl(categoryCode);
    const cacheKey = canonicalUrl;

    const cached = this.serviceRequestCodesCache.get(cacheKey);
    if (cached) return cached;

    const searchUrl = `${this.backendApiEndpoint}/ValueSet?url=${encodeURIComponent(canonicalUrl)}&_count=1000`;

    const request$ = this.http.get<any>(searchUrl).pipe(
      map((data: any) => {
        console.log('Valueset returned of ', canonicalUrl, data);
        const concepts = data?.entry?.[0]?.resource?.compose?.include?.[0]?.concept;
        if (!Array.isArray(concepts)) return [];

        return concepts
          .filter((concept: any) => {
            const activeProperty = concept?.property?.find((p: any) => p?.code === 'active');
            const isActive = activeProperty?.valueBoolean !== false;
            return isActive;
          })
          .map((concept: any) => ({
            code: concept?.code,
            display: concept?.display,
            system: canonicalUrl
          }))
          .filter((opt: ServiceRequestCodeOption) => !!opt.code);
      }),
      catchError(() => of([])),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.serviceRequestCodesCache.set(cacheKey, request$);
    return request$;
  }

  private buildServiceRequestCodesCanonicalUrl(categoryCode?: string | null): string {
    const base = `${this.terminologyCanonicalUrlBase}/CodeSystem/ServiceRequest/ServiceRequestCodes`;
    const category = (categoryCode || '').trim();
    if (!category) return base;
    console.log(`${base}${this.toTitleCaseWord(category)}`);
    return `${base}${this.toTitleCaseWord(category)}`;
  }

  private toTitleCaseWord(input: string): string {
    const trimmed = (input || '').trim();
    if (!trimmed) return '';
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  }

  getResourceData(resourceType: string): Observable<FhirResource[]> {
    const url = `${this.backendApiEndpoint}/${resourceType}?_count=1000`;
    return this.http.get<Bundle>(url).pipe(
      map((response: Bundle) => response?.entry?.map((resourceEntry: BundleEntry<FhirResource>) => this.retrieveResourceFromEntry(resourceEntry) || []) || [])
    );
  }

  filterResourceByACodeableConceptfield(resources: FhirResource[], fieldName: string, fieldValue: Coding | string): FhirResource[] {
    return resources.filter((resource: any) => {
      const conceptFieldValue = resource[fieldName] as CodeableConcept;
      if (!conceptFieldValue) {
        return false;
      }
      if (typeof fieldValue === 'string') {
        return this.searchCodeableConceptByDisplayOrText(conceptFieldValue, fieldValue);
      } else {
        return (conceptFieldValue.coding || []).some((coding: any) => {
          return coding.code === fieldValue.code && coding.system === fieldValue.system;
        });

      }
    });
  }

  /**
   * Filter medication bundle entries to return only those that are:
   * - Active (status === 'active')
   * - In stock (totalRemaining extension > 0)
   * - Not expired (batch.expirationDate > now, if present)
   *
   * @param entries Array of FHIR Bundle entries containing Medication resources
   * @returns Filtered array of bundle entries with available medications
   *
   * @example
   * const availableMeds = this.filterAvailableMedications(bundle.entry);
   */
  filterAvailableMedications(entries: BundleEntry<FhirResource>[]): BundleEntry<FhirResource>[] {
    if (!entries || !Array.isArray(entries)) {
      return [];
    }

    const now = new Date();

    return entries.filter((entry: BundleEntry<FhirResource>) => {
// $#$      // /* alert("in filtering"); */
      const medication = this.retrieveResourceFromEntry(entry) as any;

      // Must be a Medication resource
      if (medication?.resourceType !== 'Medication') {
        return false;
      }
// $#$      // /* alert("still here"); */
      // Check status is active
      if (medication.status?.toLowerCase() !== 'active') {
        return false;
      }
// $#$      // /* alert("still here active"); */

// $#$      // /* alert("medication:" + JSON.stringify(medication.batch) */);
      // Check expiration date if batch exists
      if (medication.batch?.expirationDate) {
        const expirationDate = new Date(medication.batch.expirationDate);
        if (!isNaN(expirationDate.getTime()) && expirationDate <= now) {
          return false; // Expired
        }
      }

      // Check totalRemaining in extensions
      if (!medication.extension || !Array.isArray(medication.extension)) {
        return false; // No extensions, no stock info
      }
      const totalRemainingExt = medication.extension.find(
        (ext: any) => this.matchesTerminologyExtension(ext?.url, 'totalRemaining')
      );

      if (!totalRemainingExt) {
        return false; // No stock tracking
      }

      // Extract quantity value
      const remainingValue = totalRemainingExt.valueQuantity?.value ??
        totalRemainingExt.valueInteger ??
        0;

      // Must have at least 1 unit remaining
      return remainingValue > 0;
    });
  }

  /**
   * Extract total remaining quantity from a Medication resource's extensions.
   *
   * @param medication FHIR Medication resource
   * @returns Object with value and unit, or null if not found
   */
  getMedicationTotalRemaining(medication: any): { value: number; unit?: string } | null {
    if (!medication?.extension || !Array.isArray(medication.extension)) {
      return null;
    }

    const totalRemainingExt = medication.extension.find(
      (ext: any) => this.matchesTerminologyExtension(ext?.url, 'totalRemaining')
    );

    if (!totalRemainingExt) {
      return null;
    }

    const value = totalRemainingExt.valueQuantity?.value ??
      totalRemainingExt.valueInteger ??
      0;

    const unit = totalRemainingExt.valueQuantity?.unit;

    return { value, unit };
  }

  findMedicationActiveBatches(referenceMed: any): any[] {
    if (!referenceMed) return [];
    const allMeds = this.stateService.orgWideResources.medications.getValue() || [];
    const normalize = (s: any) => (s ?? '').toString().trim().toLowerCase().replace(/\/+$/, '');
    const refCode = normalize(referenceMed.code?.coding?.[0]?.code);
    const refSystem = normalize(referenceMed.code?.coding?.[0]?.system);
    const refText = normalize(referenceMed.code?.text);

    return allMeds.filter((m: any) => {
       // Exclude inactive
       if (m.status !== 'active') return false;

       // 1. Check expiration date (exclude if expired)
       if (m.batch?.expirationDate) {
           const expDate = new Date(m.batch.expirationDate);
           if (expDate <= new Date()) {
               return false; // Expired
           }
       }

       // 2. Check code matching
       const code = normalize(m.code?.coding?.[0]?.code);
       const system = normalize(m.code?.coding?.[0]?.system);
       const text = normalize(m.code?.text);
       if (refCode && refSystem && code === refCode && system === refSystem) return true;
       if (refText && text === refText) return true;
       return false;
    });
  }

  findMatchingMedications(referenceMed: any, options: MedicationMatchOptions = {}): Observable<Medication[]> {
    if (!referenceMed) return of([]);
    
    const normalize = (s: any) => (s ?? '').toString().trim().toLowerCase().replace(/\/+$/, '');
    const refCode = normalize(referenceMed.code?.coding?.[0]?.code);
    const refSystem = normalize(referenceMed.code?.coding?.[0]?.system);
    const refText = normalize(referenceMed.code?.text);
    
    const queries: Observable<Medication[]>[] = [];

    // 1. Query by exact Code and System
    if (refCode && refSystem) {
      queries.push(this.fetchAll<Medication>({
        resourceType: 'Medication',
        checkOrgWideState: false, // Bypass cache to avoid pulling 10,000 records
        updateOrgWideState: false,
        queries: { status: 'active', code: `${refSystem}|${refCode}` }
      }));
    }

    // 2. Query by Text Match (side-by-side)
    if (refText) {
      queries.push(this.fetchAll<Medication>({
        resourceType: 'Medication',
        checkOrgWideState: false, // Bypass cache
        updateOrgWideState: false,
        queries: { status: 'active', 'code:text': refText }
      }));
    }

    if (queries.length === 0) return of([]);

    return forkJoin(queries).pipe(
      map(results => {
        // Flatten results and remove duplicates by ID
        const allMeds = results.flat();
        const uniqueMap = new Map<string, Medication>();
        allMeds.forEach(m => { if (m.id) uniqueMap.set(m.id, m); });
        const mergedMeds = Array.from(uniqueMap.values());

        // Extract reference batch ID
        const getBatchId = (m: any) => normalize(m.batch?.lotNumber);
        const refBatchId = getBatchId(referenceMed);

        return mergedMeds.filter(m => {
          // 1. Check Expiration
          if (options.excludeExpired && m.batch?.expirationDate) {
             const expDate = new Date(m.batch.expirationDate);
             if (expDate <= new Date()) return false;
          }

          // 2. Check Stock
          if (options.excludeOutOfStock) {
            const trExt = m.extension?.find((e: any) => typeof e?.url === 'string' && e.url.endsWith('totalRemaining'));
            const remaining = Number(trExt?.valueQuantity?.value ?? trExt?.valueInteger ?? trExt?.valueString ?? 0);
            if (remaining <= 0) return false;
          }

          // 3. Check Clinical Equivalence (Root-level Amount/Strength)
          const refNum = referenceMed.amount?.numerator?.value;
          const refUnit = referenceMed.amount?.numerator?.unit?.toLowerCase();
          const mNum = m.amount?.numerator?.value;
          const mUnit = m.amount?.numerator?.unit?.toLowerCase();
          
          // Explicit Contradiction Logic: Only reject if BOTH have a recorded strength and they do not match.
          const hasRootContradiction = (refNum !== undefined && mNum !== undefined) && 
                                       (refNum !== mNum || refUnit !== mUnit);
          if (hasRootContradiction) return false;

          // 4. Check Clinical Equivalence (Ingredient-level)
          // Helper to get a stable comparable string from a FHIR CodeableConcept (fallback text -> display -> code)
          const getConceptString = (cc: any) => {
            if (!cc) return '';
            if (cc.text?.trim()) return cc.text.trim().toLowerCase();
            const coding = cc.coding?.[0];
            if (coding?.display?.trim()) return coding.display.trim().toLowerCase();
            if (coding?.code?.trim()) return coding.code.trim().toLowerCase();
            return '';
          };

          // Filter out empty/blank ingredients that might be sent by the UI form array
          const isValidIng = (i: any) => !!getConceptString(i.itemCodeableConcept);
          const refIngs = (referenceMed.ingredient || []).filter(isValidIng);
          const mIngs = (m.ingredient || []).filter(isValidIng);
          
          // Explicit Contradiction Logic: Only reject if BOTH have recorded ingredients, but the lengths mismatch.
          if (refIngs.length > 0 && mIngs.length > 0 && refIngs.length !== mIngs.length) return false;

          for (let i = 0; i < refIngs.length; i++) {
            const rI = refIngs[i];
            const rString = getConceptString(rI.itemCodeableConcept);
            
            // Find the matching ingredient in the other drug by name/code
            const mI = mIngs.find((mi: any) => getConceptString(mi.itemCodeableConcept) === rString);
            
            // If the other batch has ingredients, but is missing this specific one, it's a contradiction.
            // If the other batch has NO ingredients at all (lazy entry), we allow it.
            if (!mI) {
               if (mIngs.length > 0) return false; // Explicit contradiction
               continue; 
            }

            // Compare the specific ingredient's strength
            const rINum = rI.strength?.numerator?.value;
            const rIUnit = rI.strength?.numerator?.unit?.toLowerCase();
            const mINum = mI.strength?.numerator?.value;
            const mIUnit = mI.strength?.numerator?.unit?.toLowerCase();

            // Explicit Contradiction Logic: Only reject if BOTH have recorded ingredient strengths and they do not match.
            const hasIngContradiction = (rINum !== undefined && mINum !== undefined) && 
                                        (rINum !== mINum || rIUnit !== mIUnit);
            if (hasIngContradiction) return false; 
          }

          // 3. Check Batch ID (if requested)
          if (options.matchBatchId) {
            const batchId = getBatchId(m);
            if (!refBatchId || !batchId || refBatchId !== batchId) return false;
          }

          // 4. Client-side waterfall match (double-check since we merged results)
          const code = normalize(m.code?.coding?.[0]?.code);
          const system = normalize(m.code?.coding?.[0]?.system);
          const text = normalize(m.code?.text);

          if (refCode && refSystem && code && system) {
             // Both have Code + System: MUST match exactly
             return (code === refCode && system === refSystem);
          } else {
             // Missing code/system: Fallback to strict text matching
             return (refText && text && text === refText);
          }
        });
      })
    );
  }

  getAggregateMedicationTotalRemaining(medication: any): { value: number; unit?: string } | null {
    if (!medication) return null;
    const base = this.getMedicationTotalRemaining(medication);
    const siblings = this.findMedicationActiveBatches(medication);
    if (!siblings || siblings.length === 0) return base;
    
    let total = 0;
    let unit = base?.unit;
    for (const sib of siblings) {
      const sibRem = this.getMedicationTotalRemaining(sib);
      if (sibRem && typeof sibRem.value === 'number') {
        total += sibRem.value;
        if (!unit && sibRem.unit) {
          unit = sibRem.unit;
        }
      }
    }
    
    return { value: total, unit };
  }

  getMedicationLowStockConfig(medication: any): { threshold?: number; unit?: string; alertsEnabled?: boolean } | null {
    if (!medication?.extension || !Array.isArray(medication.extension)) {
      return null;
    }

    const thresholdExt = medication.extension.find((ext: any) => this.matchesTerminologyExtension(ext?.url, 'lowStockThreshold'));
    const alertsExt = medication.extension.find((ext: any) => this.matchesTerminologyExtension(ext?.url, 'lowStockAlertsEnabled'));

    const thresholdRaw = thresholdExt?.valueQuantity?.value ?? thresholdExt?.valueInteger ?? null;
    const threshold = thresholdRaw === null || thresholdRaw === undefined ? null : Number(thresholdRaw);
    const normalizedThreshold = threshold !== null && Number.isFinite(threshold) && threshold >= 0 ? threshold : null;
    const alertsEnabled = typeof alertsExt?.valueBoolean === 'boolean' ? alertsExt.valueBoolean : undefined;

    if (normalizedThreshold === null && alertsEnabled === undefined) {
      return null;
    }

    return {
      ...(normalizedThreshold !== null ? { threshold: normalizedThreshold } : {}),
      ...(thresholdExt?.valueQuantity?.unit ? { unit: thresholdExt.valueQuantity.unit } : {}),
      ...(alertsEnabled !== undefined ? { alertsEnabled } : {}),
    };
  }

  getSupplyLowStockConfig(resource: any): { threshold?: number; unit?: string; alertsEnabled?: boolean } | null {
    if (!resource?.extension || !Array.isArray(resource.extension)) {
      return null;
    }

    const thresholdExt = resource.extension.find((ext: any) => this.matchesTerminologyExtension(ext?.url, 'lowStockThreshold'));
    const alertsExt = resource.extension.find((ext: any) => this.matchesTerminologyExtension(ext?.url, 'lowStockAlertsEnabled'));

    const thresholdRaw = thresholdExt?.valueQuantity?.value ?? thresholdExt?.valueInteger ?? null;
    const parsedThreshold = thresholdRaw === null || thresholdRaw === undefined ? null : Number(thresholdRaw);
    const normalizedThreshold = parsedThreshold !== null && Number.isFinite(parsedThreshold) && parsedThreshold >= 0 ? parsedThreshold : null;
    const alertsEnabled = typeof alertsExt?.valueBoolean === 'boolean' ? alertsExt.valueBoolean : undefined;

    if (normalizedThreshold === null && alertsEnabled === undefined) {
      return null;
    }

    return {
      ...(normalizedThreshold !== null ? { threshold: normalizedThreshold } : {}),
      ...(thresholdExt?.valueQuantity?.unit ? { unit: thresholdExt.valueQuantity.unit } : {}),
      ...(alertsEnabled !== undefined ? { alertsEnabled } : {}),
    };
  }

  isBelowLowStockThreshold(remaining: number | null | undefined, threshold?: number | null): boolean {
    if (threshold === null || threshold === undefined) {
      return false;
    }
    if (remaining === null || remaining === undefined) {
      return false;
    }
    const numericRemaining = Number(remaining);
    const numericThreshold = Number(threshold);
    if (!Number.isFinite(numericRemaining) || !Number.isFinite(numericThreshold)) {
      return false;
    }
    return numericRemaining <= numericThreshold;
  }

  shouldSendLowStockAlert(
    remaining: number | null | undefined,
    config?: { threshold?: number | null; alertsEnabled?: boolean | null }
  ): boolean {
    if (!config || config.alertsEnabled !== true) {
      return false;
    }
    return this.isBelowLowStockThreshold(remaining, config.threshold);
  }

  private matchesTerminologyExtension(url: string | undefined, key: string): boolean {
    if (!url) {
      return false;
    }
    // Often URLs are just the key or end with /StructureDefinition/key or Medication-key
    if (url.includes(key)) {
      return true;
    }
    const suffix = `/StructureDefinition/${key}`;
    if (url.endsWith(suffix)) {
      return true;
    }
    if (this.terminologyCanonicalUrlBase) {
      return url === `${this.terminologyCanonicalUrlBase}${suffix}`;
    }
    return false;
  }

  /**
   * Check if a medication is available for dispensing.
   * Convenience method for single medication checks.
   *
   * @param medication FHIR Medication resource
   * @returns true if medication is active, in stock, and not expired
   */
  isMedicationAvailable(medication: any): boolean {
    if (!medication || medication.resourceType !== 'Medication') {
      return false;
    }

    // Wrap in bundle entry format for filter method
    const entry: BundleEntry<FhirResource> = { resource: medication };
    const filtered = this.filterAvailableMedications([entry]);

    return filtered.length > 0;
  }

  /**
   * Map a "display" duration unit (your durationUnits[][1]) to the coded unit (durationUnits[][0]).
   * Examples:
   * - "day" -> "d"
   * - "week" -> "wk"
   */
  durationUnitDisplayToCode(input: string | null | undefined): string | undefined {
    const v = (input ?? '').toString().trim().toLowerCase();
    if (!v) return undefined;

    // already a known code?
    const asCode = this.durationUnits.find(u => (u?.[0] ?? '').toLowerCase() === v);
    if (asCode) return asCode[0];

    // match display (2nd element)
    const hit = this.durationUnits.find(u => (u?.[1] ?? '').toLowerCase() === v);
    return hit?.[0] ?? undefined;
  }

  /**
   * Optional reverse: map coded unit (durationUnits[][0]) to display (durationUnits[][1]).
   * Example: "wk" -> "week"
   */
  durationUnitCodeToDisplay(input: string | null | undefined): string | undefined {
    const v = (input ?? '').toString().trim().toLowerCase();
    if (!v) return undefined;
    const hit = this.durationUnits.find(u => (u?.[0] ?? '').toLowerCase() === v);
    return hit?.[1] ?? undefined;
  }

  /**
   * Convert common timing.code abbreviations (BID/TID/QD/etc) to timing.repeat.frequency + period(+periodUnit).
   * FHIR note:
   * - This is an approximation for computability; keep timing.code for UI/display.
   */
  mapTimingCodeToFrequencyPeriod(code: string | null | undefined): { frequency: number; period: number; periodUnit: 'd' | 'h' | 'wk' | 'mo' } | null {
    const c = (code ?? '').toString().trim().toUpperCase();
    if (!c) return null;

    // Common HL7 v3 GTSAbbreviation-ish mappings (approximate)
    switch (c) {
      case 'QD':
        return { frequency: 1, period: 1, periodUnit: 'd' };
      case 'BID':
        return { frequency: 2, period: 1, periodUnit: 'd' };
      case 'TID':
        return { frequency: 3, period: 1, periodUnit: 'd' };
      case 'QID':
        return { frequency: 4, period: 1, periodUnit: 'd' };

      case 'QOD':
        return { frequency: 1, period: 2, periodUnit: 'd' };

      case 'Q1H':
        return { frequency: 1, period: 1, periodUnit: 'h' };
      case 'Q2H':
        return { frequency: 1, period: 2, periodUnit: 'h' };
      case 'Q3H':
        return { frequency: 1, period: 3, periodUnit: 'h' };
      case 'Q4H':
        return { frequency: 1, period: 4, periodUnit: 'h' };
      case 'Q6H':
        return { frequency: 1, period: 6, periodUnit: 'h' };
      case 'Q8H':
        return { frequency: 1, period: 8, periodUnit: 'h' };

      case 'WK':
        return { frequency: 1, period: 1, periodUnit: 'wk' };
      case 'MO':
        return { frequency: 1, period: 1, periodUnit: 'mo' };

      // AM/PM/BED are times-of-day, not frequencies; skip auto-mapping.
      case 'AM':
      case 'PM':
      case 'BED':
      default:
        return null;
    }
  }

  /**
   * Enrich a Dosage.timing by inferring repeat.frequency/period from timing.code if possible.
   * Does NOT remove timing.code.
   */
  applyTimingCodeToRepeat(timing: Dosage['timing'] | null | undefined): Dosage['timing'] | undefined {
    if (!timing) return timing ?? undefined;

    const coding0 = (timing as any)?.code?.coding?.[0];
    const code =
      (coding0?.code ?? (timing as any)?.code?.text ?? '').toString();

    const mapped = this.mapTimingCodeToFrequencyPeriod(code);
    if (!mapped) return timing;

    const existingRepeat = (timing as any).repeat ?? {};
    return {
      ...(timing as any),
      repeat: {
        ...existingRepeat,
        frequency: existingRepeat.frequency ?? mapped.frequency,
        period: existingRepeat.period ?? mapped.period,
        periodUnit: existingRepeat.periodUnit ?? mapped.periodUnit
      }
    } as any;
  }

  /**
   * Normalize a duration unit (code or display) to a friendly display string.
   * Accepts: "d" | "day" | "wk" | "week" etc.
   * Returns: "day" | "week" etc (or original input if unknown).
   */
  durationUnitToNiceDisplay(input: string | null | undefined): string | undefined {
    const v = (input ?? '').toString().trim();
    if (!v) return undefined;

    // Try code->display first
    const byCode = this.durationUnitCodeToDisplay(v);
    if (byCode) return byCode;

    // Try display->code->display (handles "day" -> "d" -> "day")
    const code = this.durationUnitDisplayToCode(v);
    const byRoundTrip = this.durationUnitCodeToDisplay(code);
    return byRoundTrip ?? v;
  }

  /**
   * Safely add an Identifier to a FHIR resource without removing existing identifiers.
   * - Normalizes resource.identifier to an array
   * - Prevents duplicates (system+value match, and optionally type.coding match)
   *
   * "Where identifier is coming from" is expressed via:
   * - identifier.system (namespace)
   * - identifier.type (CodeableConcept: coding[0].system/code)
   * - identifier.assigner (Reference to org/person that assigned it)
   */
  upsertIdentifier<T extends { identifier?: Identifier | Identifier[] }>(
    resource: T,
    identifier: Identifier,
    opts?: {
      /** If true (default), also require type match when both sides have type codings */
      matchTypeWhenPresent?: boolean;
    }
  ): T {
    if (!resource || !identifier) return resource;

    const matchTypeWhenPresent = opts?.matchTypeWhenPresent ?? true;

    // normalize identifier array (do not remove other identifiers)
    const current: Identifier[] = Array.isArray(resource.identifier)
      ? (resource.identifier as Identifier[])
      : resource.identifier
        ? [resource.identifier as Identifier]
        : [];

    // basic validity: don't add empty identifier objects
    const sys = (identifier.system ?? '').toString().trim();
    const val = (identifier.value ?? '').toString().trim();
    if (!sys && !val) return resource;

    const exists = current.some((id) =>
      this.isSameIdentifier(id, identifier, matchTypeWhenPresent)
    );

    if (!exists) {
      resource.identifier = [...current, identifier];
    } else {
      // keep as-is (do not overwrite) to avoid mutating existing identifier details
      resource.identifier = current;
    }

    return resource;
  }

  private isSameIdentifier(a: Identifier | null | undefined, b: Identifier | null | undefined, matchTypeWhenPresent: boolean): boolean {
    if (!a || !b) return false;

    const aSys = (a.system ?? '').toString().trim();
    const bSys = (b.system ?? '').toString().trim();
    const aVal = (a.value ?? '').toString().trim();
    const bVal = (b.value ?? '').toString().trim();

    // Most reliable match in FHIR identifiers: system + value
    const sysValMatch = (!!aSys || !!bSys) ? (aSys === bSys && aVal === bVal) : (aVal === bVal);
    if (!sysValMatch) return false;

    if (!matchTypeWhenPresent) return true;

    // If either side has no type coding, treat sys+value as enough
    const aType = this.firstTypeCodingKey(a);
    const bType = this.firstTypeCodingKey(b);
    if (!aType || !bType) return true;

    return aType === bType;
  }

  private firstTypeCodingKey(id: Identifier | null | undefined): string | null {
    const c = id?.type?.coding?.[0];
    const sys = (c?.system ?? '').toString().trim();
    const code = (c?.code ?? '').toString().trim();
    if (!sys && !code) return null;
    return `${sys}|${code}`;
  }

  /**
   * Convenience builder for Identifier with "source" fields.
   * If `opts.value` is missing/blank, this will generate a value using resourceType/prefix + date + sequence.
   */
  buildIdentifier(opts: {
    system: string;

    /** Optional; if missing we generate it */
    value?: string;

    /** Required only when value is not provided */
    resourceType?: string;

    /** Optional value prefix; defaults to resourceType uppercased */
    prefix?: string;

    /** Optional identifier kind (type) */
    typeCoding?: { system?: string; code?: string; display?: string };

    /** Optional issuer / assigner */
    assigner?: { reference?: string; display?: string };

    /** Optional human label */
    typeText?: string;

    /** Optional period */
    period?: { start?: string; end?: string };
  }): Identifier {
    const system = (opts.system ?? '').toString().trim();

    let value = (opts.value ?? '').toString().trim();
    if (!value) {
      const rt = (opts.resourceType ?? '').toString().trim();
      if (!rt) {
        throw new Error('buildIdentifier: value is missing, so resourceType is required to generate it.');
      }

      const prefix = (opts.prefix ?? rt).toString().trim().toUpperCase();

      const now = new Date();
      const yyyymmdd = now.toISOString().slice(0, 10).replace(/-/g, '');

      // re-use your existing sequence generator
      const seq = this.generateSequenceNumber(6);

      // ex: MEDICATIONREQUEST-20251225-000123
      value = `${prefix}-${yyyymmdd}-${seq}`;
    }

    const assigner: Reference | undefined = opts.assigner?.reference || opts.assigner?.display
      ? { reference: opts.assigner?.reference, display: opts.assigner?.display }
      : undefined;

    return {
      system,
      value,
      ...(opts.typeCoding || opts.typeText ? {
        type: {
          ...(opts.typeCoding ? { coding: [{ ...opts.typeCoding }] as any } : {}),
          ...(opts.typeText ? { text: opts.typeText } : {})
        }
      } : {}),
      ...(assigner ? { assigner } : {}),
      ...(opts.period ? { period: { ...opts.period } as any } : {})
    };
  }

  /** Example: how to call buildIdentifier() + upsertIdentifier() */
  exampleAddGeneratedIdentifierToMedicationRequest(mr: any) {
    // 1) build identifier (value auto-generated because we omit `value`)
    const identifier = this.buildIdentifier({
      system: `${this.backendApiEndpoint}/identifier/medication-request`,
      resourceType: 'MedicationRequest', // REQUIRED when value is not supplied
      prefix: 'MR', // optional; defaults to resourceType uppercased
      typeCoding: {
        system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
        code: 'PLAC',
        display: 'Placer Identifier'
      },
      typeText: 'Medication Request Number',
      assigner: { display: 'e-Likita' }
    });

    // 2) safely add to mr.identifier without removing existing ones and without duplicating
    this.upsertIdentifier(mr, identifier);

    return mr;
  }

  /**
   * Extract Medication ingredients into a human-friendly string for dropdown labels.
   *
   * Output example:
   * "Ingredients: Paracetamol • Caffeine"
   *
   * Notes:
   * - Prefers Medication.ingredient[].itemCodeableConcept/itemReference
   * - Falls back to Medication.code when ingredient array is missing
   */
  extractMedicationIngredients(
    medication: any,
    opts?: {
      delimiter?: string;          // default: " • "
      includePrefix?: boolean;     // default: true
      prefix?: string;             // default: "Ingredients"
      maxItems?: number;           // default: 6
      /**
       * If true, only include ingredients where ingredient.isActive === true.
       * If false, include all ingredients.
       * Default: true
       */
      onlyActive?: boolean;
    }
  ): string {
    const delimiter = (opts?.delimiter ?? ' • ').toString();
    const includePrefix = opts?.includePrefix ?? true;
    const prefix = (opts?.prefix ?? 'Ingredients').toString().trim();
    const maxItems = Number.isFinite(opts?.maxItems as any) ? Number(opts?.maxItems) : 6;
    const onlyActive = opts?.onlyActive ?? true;

    const clean = (v: any) => (v ?? '').toString().trim();

    const pickCodeableConceptLabel = (cc: any): string => {
      if (!cc) return '';
      const text = clean(cc.text);
      if (text) return text;
      const coding0 = Array.isArray(cc.coding) ? cc.coding[0] : null;
      return clean(coding0?.display || coding0?.code || '');
    };

    const pickReferenceLabel = (ref: any): string => {
      if (!ref) return '';
      const disp = clean(ref.display);
      if (disp) return disp;
      const r = clean(ref.reference);
      if (!r) return '';
      const parts = r.split('/');
      return clean(parts[parts.length - 1] || r);
    };

    const fmtQuantity = (q: any): string => {
      if (!q) return '';
      const v = q.value ?? q.valueDecimal ?? q.valueInteger;
      const unit = clean(q.unit || q.code);
      const n = v === undefined || v === null || v === '' ? '' : String(v);
      return [n, unit].filter(Boolean).join(' ').trim();
    };

    const fmtRatio = (r: any): string => {
      if (!r) return '';
      const num = fmtQuantity(r.numerator);
      const den = fmtQuantity(r.denominator);
      if (num && den) return `${num} / ${den}`;
      return num || den || '';
    };

    const fmtStrength = (ing: any): string => {
      // FHIR R4 Medication.ingredient.strength[x] is typically Ratio, but some servers use Quantity.
      const ratio = ing?.strengthRatio ?? ing?.strength;
      const qty = ing?.strengthQuantity;
      const out = fmtRatio(ratio) || fmtQuantity(qty);
      return clean(out);
    };

    const rawIngredients = Array.isArray(medication?.ingredient) ? medication.ingredient : [];

    const filteredIngredients = rawIngredients.filter((ing: any) => {
      if (!onlyActive) return true;
      // only accept explicit true; ignore false
      return ing?.isActive === true;
    });

    const namesFromIngredients = filteredIngredients
      .map((ing: any) => {
        const cc = ing?.itemCodeableConcept;
        const ref = ing?.itemReference;
        const item = ing?.item;

        const name =
          cc ? pickCodeableConceptLabel(cc)
            : ref ? pickReferenceLabel(ref)
              : (item?.coding || item?.text) ? pickCodeableConceptLabel(item)
                : item?.reference ? pickReferenceLabel(item)
                  : '';

        const strength = fmtStrength(ing);

        const label = [clean(name), strength].filter(Boolean).join(' ').trim();
        return label;
      })
      .map(clean)
      .filter(Boolean);

    // Fallback: Medication.code (unchanged behavior)
    // const fallbackFromCode = (() => {
    //   const cc = medication?.code;
    //   const label = pickCodeableConceptLabel(cc);
    //   return label ? [label] : [];
    // })();

    // const combined = (namesFromIngredients.length ? namesFromIngredients : fallbackFromCode)
    const combined = namesFromIngredients
      .map((s: string) => s.replace(/\s+/g, ' ').trim())
      .filter(Boolean);

    const deduped: string[] = [];
    const seen = new Set<string>();
    for (const s of combined) {
      const key = s.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(s);
      if (deduped.length >= maxItems) break;
    }

    if (!deduped.length) return '';

    const core = deduped.join(delimiter);
    return "(" + (includePrefix ? `${prefix}: ${core}` : core) + ")";
  }

  /**
   * Get all query parameters that led to the current route/component.
   *
   * Handles:
   * - normal Angular navigation (ActivatedRoute.snapshot.queryParamMap)
   * - hard refresh (window.location.search)
   * - double/triple encoded URLs (bounded decode loop)
   * - "packed" query strings inside a single param value (e.g. ?returnUrl=%2Fapp%2Fx%3Fa%3D1%26b%3D2)
   */
  getAllQueryParamsNormalized(
    route?: ActivatedRoute | null,
    opts?: {
      /** max decode iterations to handle double encoded URLs */
      decodeIterations?: number;
      /** if true, attempt to parse nested query strings found in param values */
      parseNestedQueries?: boolean;
    }
  ): Record<string, string | string[]> {
    const decodeIterations = opts?.decodeIterations ?? 3;
    const parseNestedQueries = opts?.parseNestedQueries ?? true;

    const safeDecode = (v: string): string => {
      let out = v ?? '';
      for (let i = 0; i < decodeIterations; i++) {
        try {
          const next = decodeURIComponent(out);
          if (next === out) break;
          out = next;
        } catch {
          break;
        }
      }
      return out;
    };

    const parseSearch = (search: string): Record<string, string | string[]> => {
      const cleaned = (search ?? '').startsWith('?') ? (search ?? '').slice(1) : (search ?? '');
      const params = new URLSearchParams(cleaned);
      const out: Record<string, string | string[]> = {};

      params.forEach((rawVal, rawKey) => {
        const key = safeDecode(rawKey);
        const val = safeDecode(rawVal);

        if (out[key] === undefined) {
          out[key] = val;
        } else if (Array.isArray(out[key])) {
          (out[key] as string[]).push(val);
        } else {
          out[key] = [out[key] as string, val];
        }
      });

      return out;
    };

    // 1) Prefer Angular route snapshot if supplied
    const fromRoute: Record<string, string | string[]> = {};
    const qp = route?.snapshot?.queryParamMap;
    if (qp) {
      qp.keys.forEach((k) => {
        const vals = qp.getAll(k).map(v => safeDecode(String(v ?? '')));
        fromRoute[safeDecode(k)] = vals.length > 1 ? vals : (vals[0] ?? '');
      });
    }

    // 2) Refresh-safe fallback from window
    const fromWindow = parseSearch(window.location.search || '');

    // Merge: route wins (it’s already decoded by Angular usually)
    const merged: Record<string, string | string[]> = { ...fromWindow, ...fromRoute };

    if (!parseNestedQueries) return merged;

    // 3) Nested query detection:
    // If any value contains "?a=1&b=2" or "a=1&b=2", parse and merge.
    const maybeParseNested = (value: string) => {
      const v = safeDecode(value);
      const idx = v.indexOf('?');
      const qs = idx >= 0 ? v.slice(idx + 1) : v;
      if (!qs.includes('=') || !qs.includes('&')) return null;
      return parseSearch(qs);
    };

    for (const [k, v] of Object.entries(merged)) {
      const values = Array.isArray(v) ? v : [v];
      values.forEach((one) => {
        if (typeof one !== 'string' || !one) return;
        const nested = maybeParseNested(one);
        if (!nested) return;

        // only fill missing keys to avoid clobbering explicit query params
        for (const [nk, nv] of Object.entries(nested)) {
          if (merged[nk] === undefined) merged[nk] = nv;
        }
      });
    }

    return merged;
  }



  findActiveDuplicateByServiceCode(service: ChargeItemDefinition) {
    const newCode = (service.code?.coding?.[0]?.code ?? '').toString().trim();
    const newSystem = (service.code?.coding?.[0]?.system ?? '').toString().trim();
    const newInstanceRef = (service.instance?.[0]?.reference ?? '').toString().trim();

    return this.fetchAll<ChargeItemDefinition>({
      resourceType: 'ChargeItemDefinition',
      queries: { status: 'active' },
      checkOrgWideState: true,
      updateOrgWideState: true,
      localFilters: [{ key: 'status', value: 'active' }]
    }).pipe(
      map(resources => resources.filter(f => {
        const statusOk = ((f?.status ?? '').toString().trim().toLowerCase() === 'active');
        if (!statusOk) return false;

        const existingInstanceRef = (f.instance?.[0]?.reference ?? '').toString().trim();

        // SCENARIO A: The new rule is for a specific batch/instance
        if (newInstanceRef) {
            // Only a duplicate if another rule points to the exact same batch!
            return existingInstanceRef === newInstanceRef;
        }

        // SCENARIO B: The new rule is generic (e.g. standard lab test, no batch)
        if (existingInstanceRef) {
            // A batch-specific rule does not conflict with a generic rule
            return false;
        }

        // Check for generic code overlap
        const codings: any[] = Array.isArray(f?.code?.coding) ? f.code.coding : [];
        const codeMatch = codings.some((c: any) => (c?.code ?? '').toString().trim() === newCode);
        if (!codeMatch) return false;

        if (!newSystem) return true;

        return codings.some(
          (c: any) =>
            (c?.code ?? '').toString().trim() === newCode &&
            (c?.system ?? '').toString().trim() === newSystem
        );
      })),
      // map((defs: ChargeItemDefinition[]) =>
      //   (defs || []).filter((d: any) => {
      //     const statusOk = ((d?.status || '').toString().toLowerCase() === 'active');
      //     if (!statusOk) return false;

      //     const codings: any[] = Array.isArray(d?.code?.coding) ? d.code.coding : [];
      //     const codeMatch = codings.some((c: any) => (c?.code ?? '').toString().trim() === code);
      //     if (!codeMatch) return false;

      //     // If system is known, prefer exact system match. Otherwise accept any system for that code.
      //     if (!system) return true;
      //     return codings.some((c: any) =>
      //       (c?.code ?? '').toString().trim() === code && (c?.system ?? '').toString().trim() === system
      //     );
      //   })
      // ),
      catchError(() => of([] as ChargeItemDefinition[]))
    );
  }

  fetchLoggedInUserOrganization(): Observable<Organization | null> {
    const orgId = this.auth.user.getValue()?.organizationId;
    if (!orgId) return of(null);
    return this.http.get<Organization>(`${this.backendUrl}/Organization/${encodeURIComponent(orgId)}`, {
      headers: { Accept: 'application/fhir+json' }
    }).pipe(
      catchError(() => of(null))
    );
  }

  getOrganizationCurrency(): Observable<Coding | null> {
    return this.fetchLoggedInUserOrganization().pipe(
      map(org => {
        if (!org) return null;
        const ext = org.extension?.find(item => item.url === `${this.terminologyBaseUrl}/StructureDefinition/defaultCurrency`);
        const coding = ext?.valueCoding;
        if (!coding) return null;
        return {
          code: coding.code,
          display: coding.display,
          system: coding.system
        };
      })
    );
  }

  getPatientHospitalId(resource: any): string | null {
    if (!resource) return null;

    const identifiers = resource.identifier || resource.identifiers;
    if (!Array.isArray(identifiers) || identifiers.length === 0) return null;

    // 1. Prioritize specific system
    const targetSystem = 'https://elikita-server.daalitech.com/fhir/identifier/pid';
    const specific = identifiers.find(id => id.system === targetSystem);
    if (specific?.value) return specific.value;

    // 2. Keyword-based prioritization (similar to AppWrapper logic)
    const normalized = identifiers.map(id => {
      const value = typeof id.value === 'string' ? id.value.trim() : (id.value?.value?.trim() || '');
      const system = (id.system || '').toLowerCase();
      const type = (id.type?.text || id.type?.coding?.[0]?.display || id.type?.coding?.[0]?.code || '').toLowerCase();
      const assigner = (id.assigner?.display || id.assigner?.reference || '').toLowerCase();
      return { value, system, type, assigner };
    }).filter(id => !!id.value);

    if (normalized.length === 0) return null;

    const keywords = ['hospital', 'mrn', 'facility', 'elikita'];
    const prioritized = normalized.find(id => {
      const haystack = `${id.system} ${id.type} ${id.assigner}`;
      return keywords.some(keyword => haystack.includes(keyword));
    });

    return (prioritized ?? normalized[0]).value;
  }

  resolveMultiBatchMedicationDeduction(
    medicationId: string,
    quantityToDeduct: number
  ): Observable<{ updatedMedications: Medication[], alerts: any[] }> {
    return this.fetchAll<Medication>({
      resourceType: 'Medication',
      checkOrgWideState: true,
      queries: { status: 'active' },
      localFilters: [{ key: 'status', value: 'active' }]
    }).pipe(
      map(allMeds => {
        const referenceMed = allMeds.find(m => m.id === medicationId);
        if (!referenceMed) {
           throw new Error(`Medication with id ${medicationId} not found.`);
        }
        
        const normalize = (s: any) => (s ?? '').toString().trim().toLowerCase().replace(/\/+$/, '');
        const refCode = normalize(referenceMed.code?.coding?.[0]?.code);
        const refSystem = normalize(referenceMed.code?.coding?.[0]?.system);
        const refText = normalize(referenceMed.code?.text);

        const matches = allMeds.filter(m => {
           // 1. Check expiration date (exclude if expired)
           if (m.batch?.expirationDate) {
               const expDate = new Date(m.batch.expirationDate);
               if (expDate <= new Date()) {
                   return false; // Expired, exclude from deduction
               }
           }

           // 2. Check code matching
           const code = normalize(m.code?.coding?.[0]?.code);
           const system = normalize(m.code?.coding?.[0]?.system);
           const text = normalize(m.code?.text);
           if (refCode && refSystem && code === refCode && system === refSystem) return true;
           if (refText && text === refText) return true;
           return false;
        });

         // Sort FIFO (by expirationDate, or just rely on id/creation order implicitly if missing)
         matches.sort((a, b) => {
            const getExtDate = (med: any) => {
               return new Date(med.batch?.expirationDate || '2099-01-01').getTime();
            };
            return getExtDate(a) - getExtDate(b);
         });

        // Sum remaining
        const getRemaining = (med: Medication) => {
           const tr = med?.extension?.find((e: any) => typeof e?.url === 'string' && e.url.endsWith('totalRemaining'));
           return Number(tr?.valueQuantity?.value ?? tr?.valueInteger ?? tr?.valueString ?? 0);
        };
        
        const setRemaining = (med: Medication, val: number) => {
           const cloned = JSON.parse(JSON.stringify(med));
           const tr = cloned?.extension?.find((e: any) => typeof e?.url === 'string' && e.url.endsWith('totalRemaining'));
           if (tr) {
              if (tr.valueQuantity) tr.valueQuantity.value = val;
              else if (tr.valueInteger !== undefined) tr.valueInteger = val;
              else tr.valueString = val.toString();
           }
           return cloned;
        };

        const aggregateRemaining = matches.reduce((sum, m) => sum + getRemaining(m), 0);
        if (quantityToDeduct > aggregateRemaining) {
           throw new Error(`Requested quantity (${quantityToDeduct}) exceeds total available stock across all batches (${aggregateRemaining}).`);
        }

        const updated: Medication[] = [];
        let leftToDeduct = quantityToDeduct;

        for (const batch of matches) {
           if (leftToDeduct <= 0) break;
           const batchRemaining = getRemaining(batch);
           if (batchRemaining <= 0) continue;

           const deduct = Math.min(leftToDeduct, batchRemaining);
           leftToDeduct -= deduct;
           
           updated.push(setRemaining(batch, batchRemaining - deduct));
        }

        const alerts: any[] = [];
        const newAggregate = aggregateRemaining - quantityToDeduct;
        
        const config = this.getMedicationLowStockConfig(referenceMed);
        if (this.isBelowLowStockThreshold(newAggregate, config?.threshold) && config?.alertsEnabled !== false) {
           const itemName = referenceMed.code?.text || referenceMed.code?.coding?.[0]?.display || 'Medication';
           const unit = config?.unit || 'pieces';
           const orgId = this.auth.user.getValue()?.organizationId || this.auth.user.getValue()?.hospitalId;

           const comm: any = {
             resourceType: 'CommunicationRequest',
             status: 'active',
             priority: 'urgent',
             authoredOn: new Date().toISOString(),
             recipient: [{ reference: `Organization/${orgId}` }],
             category: [{ coding: [{ system: 'http://terminology.hl7.org/CodeSystem/communication-category', code: 'alert', display: 'Alert' }] }],
             payload: [{ contentString: `Low stock alert: ${itemName} will have ${newAggregate}${unit ? ' ' + unit : ''} after dispensing. Threshold: ${config?.threshold || 0}${unit ? ' ' + unit : ''}.` }],
             _internalAlertData: {
                itemName,
                remaining: newAggregate,
                threshold: config?.threshold || 0,
                unit,
                resourceType: 'Medication',
                url: '/app/medication'
             }
           };
           alerts.push(comm);
        }

        return { updatedMedications: updated, alerts };
      })
    );
  }
}

/**
 * Normalizes a FHIR Bundle pagination next link (which may contain an upstream Azure/HAPI FHIR
 * server's absolute host or mismatched path prefixes) so that it routes safely through the target
 * proxy (.NET proxy or NodeJS proxy).
 */
export function normalizeFhirNextLinkUrl(url: string | null | undefined, targetEndpoint: string): string | null {
  const trimmed = (url || '').trim();
  const targetTrimmed = (targetEndpoint || '').trim();
  if (!trimmed || !targetTrimmed) return trimmed || null;

  try {
    const original = new URL(trimmed);
    const target = new URL(targetTrimmed);
    const origin = `${target.protocol}//${target.host}`;
    const targetPath = target.pathname.replace(/\/$/, '');

    // Strip known proxy/FHIR prefixes from original path so they are not duplicated
    // when targetPath is prepended (e.g. /authBackend/fhir, /nodejsProxy/api/fhir, /api/fhir, /fhir)
    let resourcePath = original.pathname;
    const knownPrefixes = [
      '/authBackend/fhir',
      '/authBackend',
      '/nodejsProxy/api/fhir',
      '/nodejsProxy',
      '/api/fhir',
      '/fhir'
    ];
    for (const prefix of knownPrefixes) {
      if (resourcePath.startsWith(prefix)) {
        resourcePath = resourcePath.substring(prefix.length);
        break;
      }
    }
    if (!resourcePath.startsWith('/')) {
      resourcePath = `/${resourcePath}`;
    }

    const normalizedPath = targetPath ? `${targetPath}${resourcePath}` : resourcePath;
    return `${origin}${normalizedPath}${original.search}${original.hash}`;
  } catch {
    return trimmed;
  }
}
